/**
 * Enhanced Bundle Builder with Image Gallery & Slider
 * Features: Product slider, image gallery with thumbnails, dynamic variant updates
 */

class EnhancedBundleBuilder {
  constructor() {
    this.currentStep = 1;
    this.totalSteps = 5;
    this.bundleData = null;
    this.selectedItems = {
      chair: null,
      desk: null,
      addons: [],
      addonSingle: null
    };
    this.bundleDiscount = 0;
    this.currentSlideIndex = 0;
    this.currentImages = [];
    this.currentChairImages = [];
    this.currentDeskImages = [];
    this.currentMainImageIndex = 0;
    this.addonChairOverrideSrc = null;
    this.chairSelectedImageSrc = null;
    
    this.init();
  }
  applyChairAddonImageOverride(src) {
    this.addonChairOverrideSrc = src || null;
    const chairSel = (this.selectedItems && this.selectedItems.chair) || null;
    if (!chairSel) return;
    const images = chairSel.images || this.currentChairImages || [];
    // Update main image: if clearing override, restore last selected chair image
    const target = this.addonChairOverrideSrc || this.chairSelectedImageSrc || null;
    this.updateChairImage(images, chairSel.product, target);
    // Update thumbnail active state to match the override (or first image)
    try {
      const thumbTarget = target || (this.toSrcArray(images)[0] || null);
      if (thumbTarget) this.updateThumbnailActiveState(thumbTarget, 'chair');
    } catch (_) {}
  }


  init() {
    this.loadBundleData();
    this.refreshDynamicSteps();
    this.markOutOfStockProducts();
    this.initializeAddonTiles();
    this.bindEvents();
    this.initializeFirstStep();
    this.initializeSliders();
    this.applyStepBadgeTexts();
    // Ensure add-on (single) list respects chair selection on load
    this.applyAddonSingleFilter();
    // Build add-on single options panel on first load (without forcing selection)
    this.renderAddonSingleDefaultOptions();
    // Align both add-on types to current chair selection on load (if any)
    this.syncAddonSingleSectionToChair();
    this.syncAddonsDefaultToChair();
  }

  // Determine which steps are available based on configured collections/products
  refreshDynamicSteps() {
    try {
      // Determine availability
      const hasAddonSingle = !!(this.bundleData && this.bundleData.products && Array.isArray(this.bundleData.products.addonSingles) && this.bundleData.products.addonSingles.length > 0);
      const hasAddons = !!(this.bundleData && this.bundleData.products && Array.isArray(this.bundleData.products.addons) && this.bundleData.products.addons.length > 0);

      // Hide/show step 3 (addon single)
      const step3 = document.querySelector('[data-step="3"][data-step-type="addonSingle"]');
      if (step3) step3.style.display = hasAddonSingle ? 'none' : 'none'; // default hidden until showStep; keep consistent
      const prog3 = document.querySelector('[data-progress-step="3"]');
      if (prog3) prog3.style.display = hasAddonSingle ? '' : 'none';

      // Hide/show step 4 (addons multi)
      const step4 = document.querySelector('[data-step="4"][data-step-type="addons"]');
      if (step4) step4.style.display = hasAddons ? 'none' : 'none'; // default hidden until showStep; keep consistent
      const prog4 = document.querySelector('[data-progress-step="4"]');
      if (prog4) prog4.style.display = hasAddons ? '' : 'none';

      // Build available steps list (always include 1,2 and 5)
      this.availableSteps = [1, 2];
      if (hasAddonSingle) this.availableSteps.push(3);
      if (hasAddons) this.availableSteps.push(4);
      this.availableSteps.push(5);
    } catch (e) {
      console.warn('refreshDynamicSteps error:', e);
      // Fallback to default
      this.availableSteps = [1,2,3,4,5];
    }
  }

  loadBundleData() {
    const dataElement = document.querySelector('[data-bundle-data]');
    if (dataElement) {
      this.bundleData = JSON.parse(dataElement.textContent);
      // New settings: separate pair and trio discounts
      this.bundlePairDiscount = (this.bundleData.settings && this.bundleData.settings.bundlePairDiscount) || 5;
      this.bundleTrioDiscount = (this.bundleData.settings && this.bundleData.settings.bundleTrioDiscount) || 7;
    }
  }

  // Stock helpers and initialization
  markOutOfStockProducts() {
    // Mark chair/desk slides
    document.querySelectorAll('.bundle-product-slide').forEach(slide => {
      try {
        const productId = slide.dataset.productId;
        const step = slide.dataset.step;
        const product = this.getProductByIdAndStep(productId, step);
        if (!this.productHasAvailableVariant(product)) {
          slide.classList.add('out-of-stock');
          slide.setAttribute('aria-disabled', 'true');
          slide.classList.remove('selected');
        }
      } catch (_) {}
    });

    // Mark add-ons without an available variant
    document.querySelectorAll('.bundle-addon-item').forEach(item => {
      const addonId = item.dataset.addonId;
      const product = this.getAddonProductById(addonId);
      if (!this.productHasAvailableVariant(product)) {
        item.classList.add('out-of-stock');
        const checkbox = item.querySelector('[data-addon-checkbox]');
        if (checkbox) {
          checkbox.checked = false;
          checkbox.disabled = true;
        }
      }
    });
  }

  getProductByIdAndStep(productId, step) {
    if (!this.bundleData || !productId) return null;
    if (step === 'chair') {
      return (this.bundleData.products.chairs || []).find(p => p.id == productId) || null;
    }
    if (step === 'desk') {
      return (this.bundleData.products.desks || []).find(p => p.id == productId) || null;
    }
    if (step === 'addonSingle') {
      return (this.bundleData.products.addonSingles || []).find(p => p.id == productId) || null;
    }
    return null;
  }

  getAddonProductById(productId) {
    if (!this.bundleData || !productId) return null;
    return (this.bundleData.products.addons || []).find(p => p.id == productId) || null;
  }

  productHasAvailableVariant(product) {
    return !!(product && product.variants && product.variants.some(v => v && v.available));
  }

  getFirstAvailableVariant(product) {
    if (!product || !product.variants || product.variants.length === 0) return null;
    return product.variants.find(v => v.available) || product.variants[0] || null;
  }

  // Prefer variant metafield override for primary image before falling back to featured image
  getVariantPrimaryImage(variant) {
    if (!variant) return null;
    const meta =
      variant.collection_card_img ||
      (variant.metafields &&
        ((variant.metafields.custom && variant.metafields.custom.collection_pro_card_img && (variant.metafields.custom.collection_pro_card_img.value || variant.metafields.custom.collection_pro_card_img)) ||
          variant.metafields.collection_pro_card_img));
    const featured = variant.featured_image
      ? typeof variant.featured_image === 'string'
        ? variant.featured_image
        : variant.featured_image.src || variant.featured_image.url || null
      : null;
    return meta || featured || null;
  }

  bindEvents() {
    // Product slide selection
    document.addEventListener('click', (e) => {
      const slide = e.target.closest('.bundle-product-slide');
      if (slide) {
        // Prevent selecting disabled/out-of-stock slides
        if (slide.classList.contains('out-of-stock') || slide.getAttribute('aria-disabled') === 'true') {
          this.showNotification('This product is out of stock', 'warning');
          return;
        }
        this.selectProduct(slide);
      }
    });

    // Variant selection
    document.addEventListener('click', (e) => {
      const variantSwatch = e.target.closest('.variant-option-swatch');
      if (variantSwatch && !variantSwatch.classList.contains('addon-variant-swatch')) {
        this.selectVariant(variantSwatch);
      }
    });

    // Gallery thumbnail click
    document.addEventListener('click', (e) => {
      const thumbnail = e.target.closest('.gallery-thumbnail');
      if (thumbnail) {
        this.selectThumbnail(thumbnail);
      }
    });

    // Add-on checkbox toggle
    document.addEventListener('change', (e) => {
      const addonCheckbox = e.target.closest('[data-addon-checkbox]');
      if (addonCheckbox) {
        this.toggleAddon(addonCheckbox);
      }
    });

    // Add-on swatch clicks
    document.addEventListener('click', (e) => {
      const addonSwatch = e.target.closest('.addon-variant-swatch');
      if (addonSwatch) {
        if (addonSwatch.hasAttribute('disabled') || addonSwatch.getAttribute('aria-disabled') === 'true') {
          e.preventDefault();
          return;
        }
        this.onAddonSwatchClick(addonSwatch);
      }
    });

    // Swatch tooltips (color/image box)
    const tooltipMove = (e) => {
      if (!this._swatchTooltipEl) return;
      const offset = 14;
      const x = (e.clientX || 0) + offset;
      const y = (e.clientY || 0) + offset;
      this._swatchTooltipEl.style.transform = `translate(${x}px, ${y}px)`;
    };

    document.addEventListener('mouseover', (e) => {
      const swatch = e.target.closest('.variant-option-swatch, .addon-variant-swatch');
      if (!swatch) return;
      // Avoid re-trigger when moving within the same element
      const from = e.relatedTarget;
      if (from && swatch.contains(from)) return;
      this.showSwatchTooltip(swatch, e);
    }, true);

    document.addEventListener('mousemove', (e) => {
      const swatch = e.target.closest('.variant-option-swatch, .addon-variant-swatch');
      if (!swatch) return;
      tooltipMove(e);
    }, true);

    document.addEventListener('mouseout', (e) => {
      const swatch = e.target.closest('.variant-option-swatch, .addon-variant-swatch');
      if (!swatch) return;
      // Ignore transitions within the same swatch
      const to = e.relatedTarget;
      if (to && swatch.contains(to)) return;
      this.hideSwatchTooltip();
    }, true);

   // Navigation
document.addEventListener('click', (e) => {
  const continueBtn = e.target.closest('[data-nav-continue]');
  const backBtn = e.target.closest('[data-nav-back]');
  const summaryBtn = e.target.closest('[data-nav-summary]');

  if (continueBtn) {
    e.preventDefault();
    e.stopPropagation();
    this.navigateNext();
    return;
  }

  if (backBtn) {
    e.preventDefault();
    e.stopPropagation();
    this.navigateBack();
    return;
  }

  if (summaryBtn) {
    e.preventDefault();
    e.stopPropagation();
    this.goToSummary(false); // ⬅ pass flag
    return;
  }
});


    // Slider navigation
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-slider-next]')) {
        this.slideNext(e.target.closest('[data-slider-next]'));
      } else if (e.target.closest('[data-slider-prev]')) {
        this.slidePrev(e.target.closest('[data-slider-prev]'));
      }
    });

    // Add to cart
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-add-to-cart]')) {
        this.addBundleToCart();
      }
    });

    // Sidebar CTA
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-sidebar-cta]')) {
        this.goToSummary();
      }
    });

    // Progress stepper: click to navigate to step
    document.addEventListener('click', (e) => {
      const stepEl = e.target.closest('.bundle-progress-stepper .progress-step');
      if (stepEl) {
        const stepNum = parseInt(stepEl.dataset.progressStep, 10);
        if (isNaN(stepNum) || stepNum < 1 || stepNum > this.totalSteps) return;

        // Gating rules: require prior selections to jump ahead
        if (stepNum >= 2 && !this.selectedItems.chair) {
          this.showNotification('Please select a chair first', 'warning');
          return;
        }
        if (stepNum >= 3 && !this.selectedItems.desk) {
          this.showNotification('Please select a desk first', 'warning');
          return;
        }

        this.currentStep = stepNum;

        // Ensure first available is selected when jumping to Chair/Desk/Add-on (single)
        if (stepNum === 1 && !this.selectedItems.chair) {
          const stepContainer = document.querySelector(`[data-step="1"]`);
          const slides = Array.from(stepContainer ? stepContainer.querySelectorAll('.bundle-product-slide') : []);
          const firstAvailable = slides.find(slide => {
            const product = this.getProductByIdAndStep(slide.dataset.productId, 'chair');
            return this.productHasAvailableVariant(product);
          });
          if (firstAvailable) this.selectProduct(firstAvailable);
        }
        if (stepNum === 2 && !this.selectedItems.desk) {
          const stepContainer = document.querySelector(`[data-step="2"]`);
          const slides = Array.from(stepContainer ? stepContainer.querySelectorAll('.bundle-product-slide') : []);
          const firstAvailable = slides.find(slide => {
            const product = this.getProductByIdAndStep(slide.dataset.productId, 'desk');
            return this.productHasAvailableVariant(product);
          });
          if (firstAvailable) this.selectProduct(firstAvailable);
        }
        // No auto-selection for optional single add-on (step 3)

        this.showStep(stepNum);
        // Ensure summary items render when jumping directly to SUMMARY
        if (stepNum === 5) {
          this.updateSummary();
        }
      }
    });
  }

  ensureSwatchTooltip() {
    if (this._swatchTooltipEl) return this._swatchTooltipEl;
    // Prefer server-rendered tooltip if present
    let el = document.getElementById('bundle-swatch-tooltip');
    if (!el) {
      el = document.createElement('div');
      el.id = 'bundle-swatch-tooltip';
      el.className = 'bundle-swatch-tooltip';
      el.innerHTML = '<span class="tooltip-swatch"></span>';
      el.style.position = 'fixed';
      el.style.zIndex = '9999';
      el.style.pointerEvents = 'none';
      el.style.display = 'none';
      document.body.appendChild(el);
    } else if (!el.querySelector('.tooltip-swatch')) {
      const inner = document.createElement('span');
      inner.className = 'tooltip-swatch';
      el.appendChild(inner);
    }
    // Fallback inline sizing if CSS not loaded
    try {
      const box = el.querySelector('.tooltip-swatch');
      if (box) {
        if (!box.style.width) box.style.width = '72px';
        if (!box.style.height) box.style.height = '72px';
        if (!box.style.borderRadius) box.style.borderRadius = '8px';
        if (!box.style.border) box.style.border = '2px solid rgba(0,0,0,0.15)';
        if (!box.style.boxShadow) box.style.boxShadow = '0 6px 18px rgba(0,0,0,0.18)';
      }
    } catch (_) {}
    this._swatchTooltipEl = el;
    return el;
  }

  showSwatchTooltip(target, e) {
    const el = this.ensureSwatchTooltip();
    const box = el.querySelector('.tooltip-swatch');
    let image = target.getAttribute('data-swatch-image');
    let color = target.getAttribute('data-swatch-color');

    // Fallback: derive from inner swatch circle styles if attributes missing
    if (!image && !color) {
      const circle = target.querySelector('.variant-color-circle');
      if (circle) {
        const cs = window.getComputedStyle(circle);
        const bgImg = cs.getPropertyValue('background-image');
        const bgColor = cs.getPropertyValue('background-color');
        if (bgImg && bgImg !== 'none') {
          // extract url("...")
          const match = bgImg.match(/url\(["']?(.*?)["']?\)/i);
          if (match && match[1]) image = match[1];
        } else if (bgColor && bgColor !== 'rgba(0, 0, 0, 0)') {
          color = bgColor;
        }
      }
    }

    box.style.background = '';
    box.style.backgroundImage = '';
    if (image) {
      box.classList.add('image');
      box.style.backgroundImage = `url(${image})`;
    } else {
      box.classList.remove('image');
      box.style.background = color || '#ccc';
    }
    el.style.display = 'block';
    const offset = 14;
    const x = (e && e.clientX) ? e.clientX + offset : 0;
    const y = (e && e.clientY) ? e.clientY + offset : 0;
    el.style.transform = `translate(${x}px, ${y}px)`;
  }

  hideSwatchTooltip() {
    if (!this._swatchTooltipEl) return;
    this._swatchTooltipEl.style.display = 'none';
  }

  initializeFirstStep() {
    this.showStep(1);
    
    // Select first available chair
    const chairSlides = Array.from(document.querySelectorAll('[data-step="1"] .bundle-product-slide'));
    const firstAvailableChair = chairSlides.find(slide => {
      const product = this.getProductByIdAndStep(slide.dataset.productId, 'chair');
      return this.productHasAvailableVariant(product);
    });
    if (firstAvailableChair) {
      console.log('🪑 Initializing first available chair:', firstAvailableChair.dataset.productTitle);
      this.selectProduct(firstAvailableChair);
    }
    
    // Select first available desk (for gallery preview)
    const deskSlides = Array.from(document.querySelectorAll('[data-step="2"] .bundle-product-slide'));
    const firstDesk = deskSlides.find(slide => {
      const product = this.getProductByIdAndStep(slide.dataset.productId, 'desk');
      return this.productHasAvailableVariant(product);
    });
    if (firstDesk) {
      // Get desk product data
      const deskProductId = firstDesk.dataset.productId;
      const deskProductHandle = firstDesk.dataset.productHandle;
      const deskProductTitle = firstDesk.dataset.productTitle;
      const deskProductPrice = parseFloat(firstDesk.dataset.productPrice);
      const deskProductImages = JSON.parse(firstDesk.dataset.productImages || '[]');
      
      // Get desk product data from slide
      let deskProductData = null;
      try {
        deskProductData = JSON.parse(firstDesk.dataset.productData || '{}');
      } catch (e) {
        console.log('Error parsing desk product data:', e);
      }

      // Find desk product in bundle data
      const deskProduct = this.bundleData.products.desks.find(p => p.id == deskProductId);
      
      // Get initial variant (first one)
      const firstDeskVariant = this.getFirstAvailableVariant(deskProduct);
      
      // Get initial images - prefer variant metafield override, then featured image
      const deskVariantPrimary = this.getVariantPrimaryImage(firstDeskVariant);
      let deskInitialImages = [];
      if (deskVariantPrimary) {
        deskInitialImages.push(deskVariantPrimary);
      }
        if (deskProductData && deskProductData.images) {
          deskProductData.images.forEach(img => {
          if (img.src && !deskInitialImages.includes(img.src)) {
              deskInitialImages.push(img.src);
            }
          });
        }
      if (deskInitialImages.length === 0) {
        deskInitialImages = deskProductImages;
      }

      // Store desk selection
      this.selectedItems.desk = {
        id: deskProductId,
        handle: deskProductHandle,
        title: deskProductTitle,
        price: deskProductPrice,
        images: deskInitialImages,
        productData: deskProductData,
        product: deskProduct,
        variantId: firstDeskVariant ? firstDeskVariant.id : null,
        selectedOptions: firstDeskVariant ? firstDeskVariant.options : []
      };

      // Update desk image in gallery
      console.log('🖥️ Initializing first desk:', deskProductTitle);
      this.updateDeskImage(deskInitialImages, deskProduct);
      // Ensure pricing and sidebar include the default desk on first load
      this.updateAllPricing();
      this.updateSidebar();
      // Sync addon default swatches with desk color-like option
      this.syncAddonsDefaultToDesk();
    }
  }

  initializeSliders() {
    document.querySelectorAll('[data-products-slider]').forEach(slider => {
      this.updateSliderButtons(slider);
    });
  }

  // Initialize add-on tiles to show the correct image for the default selected variant options
  initializeAddonTiles() {
    try {
      document.querySelectorAll('.bundle-addon-item').forEach(addonItem => {
        const addonId = addonItem.dataset.addonId;
        const product = this.bundleData && this.bundleData.products && this.bundleData.products.addons
          ? this.bundleData.products.addons.find(p => p.id == addonId)
          : null;
        if (!product) return;

        // Determine selected options from swatches (preferred) or selects
        let selectedOptions = [];
        const swatchGroups = addonItem.querySelectorAll('.addon-variant-swatches');
        if (swatchGroups && swatchGroups.length > 0) {
          swatchGroups.forEach(g => {
            const active = g.querySelector('.addon-variant-swatch.active');
            if (active) selectedOptions.push(active.dataset.optionValue);
          });
        }
        if (selectedOptions.length === 0) {
          addonItem.querySelectorAll('.addon-variant-select').forEach(sel => selectedOptions.push(sel.value));
        }
        const variant = product.variants.find(v => v.options.every((opt, idx) => opt === selectedOptions[idx])) || null;

        const imgEl = addonItem.querySelector('.addon-image img');
        if (imgEl) {
          let src = null;
          const primaryOverride = this.getVariantPrimaryImage(variant);
          if (primaryOverride) {
            src = primaryOverride;
          } else if (variant && variant.featured_image && (variant.featured_image.src || typeof variant.featured_image === 'string')) {
            src = typeof variant.featured_image === 'string' ? variant.featured_image : variant.featured_image.src;
          }
          if (!src && product.featuredImage) src = product.featuredImage;
          if (src) {
            imgEl.src = this.getImageUrl(src, 200);
          }
        }

        // Reflect selected values beside labels
        addonItem.querySelectorAll('.addon-variant-swatches').forEach(g => {
          const optionName = g.getAttribute('data-addon-option');
          const active = g.querySelector('.addon-variant-swatch.active');
          const value = active ? active.dataset.optionValue : null;
          const target = addonItem.querySelector(`[data-addon-selected-value="${optionName}"]`);
          if (target && value) target.textContent = value;
        });

        // Disable OOS combinations
        this.refreshAddonAvailability(addonItem, product);
      });
    } catch (e) {
      console.warn('initializeAddonTiles error:', e);
    }
  }

  // Build the Add-on Single options panel from the first visible tile (without forcing selection)
  renderAddonSingleDefaultOptions() {
    try {
      const stepEl = document.querySelector('[data-step-type="addonSingle"]');
      if (!stepEl) return;
      const variantSection = stepEl.querySelector('[data-variant-section]');
      if (!variantSection) return;
      // If already has options, do nothing
      const optionsEl = variantSection.querySelector('[data-variant-options]');
      if (optionsEl && optionsEl.children.length > 0) return;
      // Prefer current selected add-on single product
      if (this.selectedItems.addonSingle && this.selectedItems.addonSingle.product) {
        this.updateVariantSection(this.selectedItems.addonSingle.product, 'addonSingle');
        return;
      }
      // Find first visible tile to use as context
      const slides = Array.from(stepEl.querySelectorAll('.bundle-product-slide[data-step="addonSingle"]'));
      if (!slides || slides.length === 0) return;
      const firstVisible = slides.find(slide => slide.style.display !== 'none') || slides[0];
      if (!firstVisible) return;
      const productId = firstVisible.dataset.productId;
      let product = null;
      try {
        product = (this.bundleData?.products?.addonSingles || []).find(p => p.id == productId) || null;
        if (!product) {
          // fallback to embedded dataset
          product = JSON.parse(firstVisible.dataset.productData || '{}');
        }
      } catch (_) {}
      if (!product) return;
      this.updateVariantSection(product, 'addonSingle');
    } catch (e) {
      console.warn('renderAddonSingleDefaultOptions error:', e);
    }
  }

  // Product Selection
  selectProduct(slide) {
    const step = slide.dataset.step;
    const stepContainer = slide.closest('.bundle-step');
    
    // Optional single add-on: allow deselect by clicking the selected tile again
    if (step === 'addonSingle' && slide.classList.contains('selected')) {
      slide.classList.remove('selected');
      this.selectedItems.addonSingle = null;
      // Keep the options visible for browsing even when not selected
      // Also clear any chair image override from add-on
      this.applyChairAddonImageOverride(null);
      this.updateAllPricing();
      this.updateSidebar();
      return;
    }
    
    // Remove selection from other slides
    stepContainer.querySelectorAll('.bundle-product-slide').forEach(s => {
      s.classList.remove('selected');
    });

    // Mark this slide as selected
    slide.classList.add('selected');

    // Get product data
    const productId = slide.dataset.productId;
    const productHandle = slide.dataset.productHandle;
    const productTitle = slide.dataset.productTitle;
    const productPrice = parseFloat(slide.dataset.productPrice);
    const productImages = JSON.parse(slide.dataset.productImages || '[]');
    
    // Get full product data from slide
    let productData = null;
    try {
      productData = JSON.parse(slide.dataset.productData || '{}');
    } catch (e) {
      console.log('Error parsing product data:', e);
    }

    // Find product in bundle data
    let products = [];
    if (step === 'chair') {
      products = this.bundleData.products.chairs;
    } else if (step === 'desk') {
      products = this.bundleData.products.desks;
    } else if (step === 'addonSingle') {
      products = this.bundleData.products.addonSingles || [];
    }
    
    const product = products.find(p => p.id == productId);

    // Guard: product must have an available variant
    if (!this.productHasAvailableVariant(product)) {
      this.showNotification('Selected product is out of stock', 'warning');
      return;
    }

    // Get initial variant (first one)
    const firstVariant = this.getFirstAvailableVariant(product);
    
    // Get initial images - prefer variant metafield override, then variant featured image
    const firstVariantPrimary = this.getVariantPrimaryImage(firstVariant);
    let initialImages = [];
    if (firstVariantPrimary) {
      initialImages.push(firstVariantPrimary);
    }
      if (productData && productData.images) {
        productData.images.forEach(img => {
        if (img.src && !initialImages.includes(img.src)) {
            initialImages.push(img.src);
          }
        });
      }
    if (initialImages.length === 0) {
      initialImages = productImages;
    }

    // Store selection
    this.selectedItems[step] = {
      id: productId,
      handle: productHandle,
      title: productTitle,
      price: productPrice,
      images: initialImages,
      productData: productData,
      product: product,
      variantId: firstVariant ? firstVariant.id : null,
      selectedOptions: firstVariant ? firstVariant.options : []
    };

    // Update image gallery (specific to step)
    if (step === 'chair') {
      this.updateChairImage(initialImages, product);
    } else if (step === 'desk') {
      this.updateDeskImage(initialImages, product);
    } else if (step === 'addonSingle') {
      // no split view update; remain as-is
    }

    // Update the selected product tile image to match the initial variant if available
    const tileImg = slide.querySelector('.slide-image img');
    if (tileImg) {
      const variantPrimary = this.getVariantPrimaryImage(firstVariant);
      const initialTileSrc = variantPrimary
        || (initialImages && initialImages[0])
        || null;
      if (initialTileSrc) {
        tileImg.src = this.getImageUrl(initialTileSrc, 150);
      }
    }

    // Update variant section
    this.updateVariantSection(product, step);

    // Update pricing
    this.updateAllPricing();
    this.updateSidebar();

    // If chair changed, re-filter add-on (single) items to match
    if (step === 'chair') {
      this.applyAddonSingleFilter();
      this.syncAddonsDefaultToChair();
      this.syncAddonSingleSectionToChair();
    }

    // If selecting an addon single product, align its variant swatches to chair color
    if (step === 'addonSingle') {
      this.syncAddonSingleSectionToChair(stepContainer);
    }
  }

  // Image Gallery - Split View
  updateImageGallery(images, product, variantImage = null) {
    const step = this.getCurrentStepType();
    
    // Determine which image to update
    if (step === 'chair') {
      this.updateChairImage(images, product, variantImage);
    } else if (step === 'desk') {
      this.updateDeskImage(images, product, variantImage);
    }
    
    // Update thumbnails for current step
    this.updateThumbnails(images, step);
  }

  updateChairImage(images, product, variantImage = null) {
    console.log('🪑 updateChairImage called:', {images, variantImage});
    
    const chairImage = document.querySelector('[data-chair-image]');
    const chairContainer = document.querySelector('.gallery-chair');
    
    if (!chairImage) {
      console.warn('⚠️ Chair image element not found!');
      return;
    }
    
    // Get image to display
    let imageToShow = null;
    const srcArray = this.toSrcArray(images);
    if (variantImage) {
      imageToShow = variantImage;
      console.log('✅ Using variant image:', imageToShow);
    } else if (srcArray && srcArray.length > 0) {
      imageToShow = srcArray[0];
      console.log('✅ Using first image from array:', imageToShow);
    } else if (product && product.featuredImage) {
      imageToShow = product.featuredImage;
      console.log('✅ Using product featured image:', imageToShow);
    }
    
    if (imageToShow) {
      console.log('🖼️ Setting chair image src to:', this.getImageUrl(imageToShow, 400));
      chairImage.style.opacity = '0';
      setTimeout(() => {
        chairImage.src = this.getImageUrl(imageToShow, 400);
        chairImage.style.opacity = '1';
        console.log('✅ Chair image updated!');
      }, 150);
      chairContainer.classList.remove('empty');
    } else {
      console.warn('⚠️ No image to show for chair!');
      chairContainer.classList.add('empty');
    }
    
    // Store for thumbnails
    this.currentChairImages = srcArray;
    // Persist user's last selected chair image when not using an add-on override
    if (!this.addonChairOverrideSrc || (variantImage && variantImage !== this.addonChairOverrideSrc)) {
      // Record only natural chair image selections
      if (imageToShow) this.chairSelectedImageSrc = imageToShow;
    }
  }

  updateDeskImage(images, product, variantImage = null) {
    console.log('🖥️ updateDeskImage called:', {images, variantImage});
    
    const deskImage = document.querySelector('[data-desk-image]');
    const deskContainer = document.querySelector('.gallery-desk');
    
    if (!deskImage) {
      console.warn('⚠️ Desk image element not found!');
      return;
    }
    
    // Get image to display
    let imageToShow = null;
    const srcArray = this.toSrcArray(images);
    if (variantImage) {
      imageToShow = variantImage;
      console.log('✅ Using variant image:', imageToShow);
    } else if (srcArray && srcArray.length > 0) {
      imageToShow = srcArray[0];
      console.log('✅ Using first image from array:', imageToShow);
    } else if (product && product.featuredImage) {
      imageToShow = product.featuredImage;
      console.log('✅ Using product featured image:', imageToShow);
    }
    
    if (imageToShow) {
      const deskSrc = variantImage || imageToShow;
      console.log('🖼️ Setting desk image src to:', deskSrc);
      deskImage.style.opacity = '0';
      setTimeout(() => {
        deskImage.src = deskSrc;
        deskImage.style.opacity = '1';
        console.log('✅ Desk image updated!');
      }, 150);
      deskContainer.classList.remove('empty');
    } else {
      console.warn('⚠️ No image to show for desk!');
      deskContainer.classList.add('empty');
    }
    
    // Store for thumbnails
    this.currentDeskImages = srcArray;
  }

  updateThumbnails(images, step) {
    const thumbnailsContainer = document.querySelector('[data-thumbnails]');
    if (!thumbnailsContainer) return;
    
    const validImages = this.toSrcArray(images);
    
    // Only show thumbnails if there are multiple images
    if (validImages.length > 1) {
      thumbnailsContainer.innerHTML = validImages.map((img, index) => `
        <div class="gallery-thumbnail ${index === 0 ? 'active' : ''}" 
             data-image-index="${index}"
             data-image-step="${step}">
          <img src="${this.getImageUrl(img, 100)}" alt="Thumbnail ${index + 1}" width="80" height="80">
        </div>
      `).join('');
    } else {
      thumbnailsContainer.innerHTML = '';
    }
  }

  updateThumbnailActiveState(variantImage, step) {
    const thumbnailsContainer = document.querySelector('[data-thumbnails]');
    if (!thumbnailsContainer) return;
    
    // Find thumbnail that matches the variant image
    const thumbnails = thumbnailsContainer.querySelectorAll('.gallery-thumbnail');
    let activeIndex = 0; // Default to first thumbnail
    
    thumbnails.forEach((thumbnail, index) => {
      const thumbnailImg = thumbnail.querySelector('img');
      if (thumbnailImg) {
        const thumbnailSrc = this.normalizeSrcForCompare(thumbnailImg.src);
        const variantSrc = this.normalizeSrcForCompare(variantImage);
        if (thumbnailSrc === variantSrc) {
          activeIndex = index;
        }
      }
    });
    
    // Update active state
    thumbnails.forEach((thumbnail, index) => {
      thumbnail.classList.toggle('active', index === activeIndex);
    });
    
    console.log('🖼️ Updated thumbnail active state to index:', activeIndex);
  }

  selectThumbnail(thumbnail) {
    const index = parseInt(thumbnail.dataset.imageIndex);
    const step = thumbnail.dataset.imageStep;
    
    console.log('🖼️ Thumbnail clicked:', {index, step});
    
    // Determine which image to update based on step
    if (step === 'chair') {
      const chairImage = document.querySelector('[data-chair-image]');
      const images = this.toSrcArray(this.currentChairImages);
      
      if (chairImage && images[index]) {
        // Record user's explicit selection
        this.chairSelectedImageSrc = images[index];
        console.log('🪑 Updating chair image from thumbnail:', images[index]);
        chairImage.style.opacity = '0';
        setTimeout(() => {
          chairImage.src = this.getImageUrl(images[index], 400);
          chairImage.style.opacity = '1';
          console.log('✅ Chair image updated from thumbnail!');
        }, 150);
      }
    } else if (step === 'desk') {
      const deskImage = document.querySelector('[data-desk-image]');
      const images = this.toSrcArray(this.currentDeskImages);
      
      if (deskImage && images[index]) {
        console.log('🖥️ Updating desk image from thumbnail:', images[index]);
        deskImage.style.opacity = '0';
        setTimeout(() => {
          deskImage.src = this.getImageUrl(images[index], 400);
          deskImage.style.opacity = '1';
          console.log('✅ Desk image updated from thumbnail!');
        }, 150);
      }
    }

    // Update active thumbnail
    document.querySelectorAll('.gallery-thumbnail').forEach(t => t.classList.remove('active'));
    thumbnail.classList.add('active');
  }

  getImageUrl(src, width) {
    if (src.includes('?')) {
      return src.replace(/\?.*/, `?width=${width}`);
    }
    return `${src}?width=${width}`;
  }

  // Helpers for color-aware image selection
  normalizeString(value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  // Alias map for common color naming conventions in filenames/metadata
  getColorAliases(colorKeyword) {
    const color = this.normalizeString(colorKeyword);
    const aliasesMap = {
      white: [
        'white', 'wht', 'wh', 'wt', 'wtwh', 'offwhite', 'off white',
        'ivory', 'cream', 'snow', 'pearl', 'alabaster', 'eggshell',
        'cloud', 'polar', 'alpine', 'blanco', 'bianco', 'blanc'
      ],
      gray: ['gray', 'grey', 'gry', 'gy', 'slate', 'ash', 'graphite', 'charcoal'],
      grey: ['grey', 'gray', 'gry', 'gy', 'slate', 'ash', 'graphite', 'charcoal'],
      black: ['black', 'blk', 'bk', 'noir', 'nero', 'negro'],
      blue: ['blue', 'blu', 'navy', 'nv'],
      red: ['red', 'rd', 'rouge', 'rosso'],
      green: ['green', 'grn', 'vert', 'verde'],
      yellow: ['yellow', 'ylw'],
      orange: ['orange', 'org', 'ora'],
      brown: ['brown', 'brn', 'walnut', 'chestnut', 'espresso', 'cafe'],
      beige: ['beige', 'bg', 'sand', 'khaki', 'tan', 'oat'],
      purple: ['purple', 'prp', 'violet'],
      pink: ['pink', 'pnk', 'rose', 'rosa'],
      teal: ['teal']
    };

    const aliases = aliasesMap[color] || [color];
    return Array.from(new Set(aliases.map(a => this.normalizeString(a))));
  }

  // Returns images that best match a color keyword using src and alt fallback
  findImagesByColor(images, colorKeyword) {
    const normalizedColor = this.normalizeString(colorKeyword);
    if (!images || images.length === 0 || !normalizedColor) return [];

    const aliases = this.getColorAliases(normalizedColor);

    // Score images by how well they match any alias for the color
    const scored = images.map(img => {
      const src = typeof img === 'string' ? img : img.src;
      const alt = typeof img === 'object' && img && img.alt ? img.alt : '';
      const base = (src || '').split('?')[0];
      const haystack = `${this.normalizeString(base)} ${this.normalizeString(alt)}`;

      let score = 0;
      for (const alias of aliases) {
        if (!alias) continue;
        const wordMatch = new RegExp(`(^|\\s)${alias}(\\s|$)`).test(haystack);
        if (wordMatch) { score = Math.max(score, 3); break; }
        if (haystack.includes(alias)) { score = Math.max(score, 2); }
      }

      return { src: typeof img === 'string' ? img : (img && img.src) || '', score };
    });

    // Sort by score desc, keep only positive matches
    const matches = scored
      .filter(x => x.score > 0 && x.src)
      .sort((a, b) => b.score - a.score)
      .map(x => x.src);

    return matches;
  }

  // Normalize URL for comparison (strip protocol and query params)
  normalizeSrcForCompare(src) {
    return (src || '').replace(/^https?:/, '').replace(/\?.*/, '');
  }

  // Normalize mixed array (objects/strings) into array of src strings
  toSrcArray(images) {
    if (!images || images.length === 0) return [];
    return images
      .map(img => (typeof img === 'string' ? img : (img && img.src) || ''))
      .filter(Boolean);
  }

  // Variant Section
  updateVariantSection(product, step) {
    const variantSection = document.querySelector(`[data-step="${this.currentStep}"] [data-variant-section]`);
    if (!variantSection || !product) return;

    const descriptionEl = variantSection.querySelector('[data-product-description]');
    const optionsEl = variantSection.querySelector('[data-variant-options]');

    // Set description
    if (descriptionEl) {
      descriptionEl.textContent = product.title || '';
    }

    // Build variant options (prefer the user's current selection; fallback to first available)
    const hasRealOptions =
      (Array.isArray(product.options) &&
        !(product.options.length === 1 && ((product.options[0] || '').toLowerCase() === 'title'))) ||
      (Array.isArray(product.optionsWithValues) &&
        product.optionsWithValues.some(o => (o.name || '').toLowerCase() !== 'title' && Array.isArray(o.values) && o.values.length > 0));

    if (optionsEl && hasRealOptions) {
      const options = this.getProductOptions(product);
      let baseSelection = [];
      const selectedForStep = step && this.selectedItems && this.selectedItems[step];
      if (selectedForStep && Array.isArray(selectedForStep.selectedOptions) && selectedForStep.selectedOptions.length > 0) {
        baseSelection = Array.from(selectedForStep.selectedOptions);
      } else {
        const defaultVariant = this.getFirstAvailableVariant(product) || product.variants[0];
        baseSelection = defaultVariant ? Array.from(defaultVariant.options) : [];
      }

      // Special case: some chairs encode Headrest in colour value (e.g., "Gray-Headrest").
      const headrestMerged = this.hasHeadrestMergedInColor(product);
      if (headrestMerged && step === 'chair') {
        const colorIdx = 0; // products with merged headrest typically have a single option at index 0
        const colorValues = options[colorIdx] && Array.isArray(options[colorIdx].values) ? options[colorIdx].values : [];
        // Build base color -> swatch info map and determine initial selection
        const colorMap = new Map();
        colorValues.forEach(v => {
          const parsed = this.parseHeadrestColorValue(v.value);
          if (!colorMap.has(parsed.base)) {
            colorMap.set(parsed.base, { value: parsed.base, swatchImage: v.swatchImage, swatchColor: v.swatchColor });
          }
        });

        const selectedParsed = this.parseHeadrestColorValue(baseSelection[colorIdx] || (colorValues[0] && colorValues[0].value) || '');
        const selectedBase = selectedParsed.base;
        const selectedHeadrest = selectedParsed.withHeadrest;

        // Render two rows: Colour and Headrest
        const colorRow = (() => {
          const values = Array.from(colorMap.values());
          return `
          <div class="variant-option-row" data-row-type="color" data-option-name="Colour">
            <div class="variant-option-label">
              <strong>Colour:</strong>
              <span data-selected-value="Colour">${selectedBase || ''}</span>
            </div>
            <div class="variant-option-swatches" data-option-name="Colour">
              ${values.map(v => {
                const isActive = (v.value || '').toLowerCase() === (selectedBase || '').toLowerCase();
                const guessed = v.swatchColor || this.guessColorFromText(v.value);
                const swatchHtml = `<span class=\"variant-color-circle ${v.swatchImage ? 'image-swatch' : ''}\" ${v.swatchImage ? `style=\"background-image:url('${v.swatchImage}')\"` : `style=\"background-color: ${guessed};\"`}></span>`;
                const dataSwatchImage = v.swatchImage ? ` data-swatch-image=\"${v.swatchImage}\"` : '';
                const dataSwatchColor = (v.swatchColor || guessed) ? ` data-swatch-color=\"${(v.swatchColor || guessed)}\"` : '';
                return `
                  <button type="button"
                          class="variant-option-swatch ${isActive ? 'active' : ''}"
                          data-row-type="color"
                          data-option-name="Colour"
                          data-option-index="${colorIdx}"
                          data-option-value="${v.value}"${dataSwatchImage}${dataSwatchColor}
                          aria-label="Colour: ${v.value}">
                    ${swatchHtml}
                  </button>`;
              }).join('')}
            </div>
          </div>`;
        })();

        const headrestRow = (() => {
          const yesActive = !!selectedHeadrest;
          const noActive = !selectedHeadrest;
          return `
          <div class="variant-option-row" data-row-type="headrest" data-option-name="Headrest">
            <div class="variant-option-label">
              <strong>Headrest:</strong>
              <span data-selected-value="Headrest">${yesActive ? 'Yes' : 'No'}</span>
            </div>
            <div class="variant-option-swatches" data-option-name="Headrest">
              <button type="button" class="variant-option-swatch variant-option-swatch--text ${yesActive ? 'active' : ''}"
                      data-row-type="headrest" data-option-name="Headrest" data-option-index="-1" data-option-value="with"
                      title="Yes" aria-label="Headrest: Yes">
                <span class="variant-text-circle">Yes</span>
              </button>
              <button type="button" class="variant-option-swatch variant-option-swatch--text ${noActive ? 'active' : ''}"
                      data-row-type="headrest" data-option-name="Headrest" data-option-index="-1" data-option-value="without"
                      title="No" aria-label="Headrest: No">
                <span class="variant-text-circle">No</span>
              </button>
            </div>
          </div>`;
        })();

        optionsEl.innerHTML = colorRow + headrestRow;
        // Flag special mode on section for selection logic
        variantSection.setAttribute('data-headrest-split', 'true');

        // Disable invalid combinations initially based on the other row's active value
        this.refreshHeadrestSplitAvailability(variantSection, product);
      } else {
        optionsEl.innerHTML = options.map((option, optionIndex) => {
        const nameLower = (option.name || '').toLowerCase();
        const isColorName = /(color|colour|couleur|colore|farbe|kleur|finish|laminate|leg|base|top|frame)/.test(nameLower);
        const values = Array.isArray(option.values) ? option.values : [];
        const normalizedValues = values.map(v => (typeof v === 'object' ? v : { value: v }));
        const hasSwatchData = normalizedValues.some(v => v.swatchImage || v.swatchColor);
        const isColorLike = isColorName || hasSwatchData;
        const selectedValue = baseSelection[optionIndex] || (normalizedValues[0] && normalizedValues[0].value) || '';
        // Always render even if only one option exists so single-option (e.g., leg) still shows
        const showRow = true;
        if (!showRow) return '';
        return `
        <div class="variant-option-row" data-option-index="${optionIndex}">
          <div class="variant-option-label">
            <strong>${option.name}:</strong>
            <span data-selected-value="${option.name}">${selectedValue}</span>
          </div>
          <div class="variant-option-swatches" data-option-name="${option.name}">
            ${normalizedValues.map((v) => {
              const disabled = !this.isCombinationAvailableWithBase(product, baseSelection, optionIndex, v.value);
              const isActive = v.value === selectedValue;
              // Prefer swatch image, else color, else fallback color map
              let swatchHtml = '';
              if (isColorLike) {
                if (v.swatchImage) {
                  swatchHtml = `<span class=\"variant-color-circle image-swatch\" style=\"background-image:url('${v.swatchImage}')\"></span>`;
                } else {
                  const guessed = v.swatchColor || this.guessColorFromText(v.value);
                  swatchHtml = `<span class=\"variant-color-circle\" style=\"background-color: ${guessed};\"></span>`;
                }
              }
              const dataSwatchImage = v.swatchImage ? ` data-swatch-image=\"${v.swatchImage}\"` : '';
              const dataSwatchColor = (v.swatchColor || (isColorLike ? this.guessColorFromText(v.value) : ''))
                ? ` data-swatch-color=\"${(v.swatchColor || this.guessColorFromText(v.value))}\"` : '';
              return `
                <button type="button" 
                        class="variant-option-swatch ${isActive ? 'active' : ''}" 
                        data-option-name="${option.name}"
                        data-option-index="${optionIndex}"
                        data-option-value="${v.value}"${dataSwatchImage}${dataSwatchColor}
                        ${disabled ? 'disabled aria-disabled=\"true\"' : ''}
                        aria-label="${option.name}: ${v.value}">
                  ${isColorLike ? swatchHtml : ''}
                </button>
              `;
            }).join('')}
          </div>
        </div>`;
        }).join('');
        variantSection.removeAttribute('data-headrest-split');
      }
    } else {
      optionsEl.innerHTML = '';
    }
  }

  isOptionCombinationAvailable(product, changedOptionIndex, chosenValue) {
    if (!product || !product.variants) return false;
    // Build a tentative selection using current selected swatches, but overriding the changed option
    const selected = this.getSelectedOptions(this.getCurrentStepType());
    const tentative = Array.from(selected);
    tentative[changedOptionIndex] = chosenValue;
    return product.variants.some(v => v.available && v.options.every((opt, idx) => !tentative[idx] || tentative[idx] === opt));
  }

  isCombinationAvailableWithBase(product, baseSelection, changedOptionIndex, chosenValue) {
    if (!product || !product.variants) return false;
    const tentative = Array.from(baseSelection);
    tentative[changedOptionIndex] = chosenValue;
    return product.variants.some(v => v.available && v.options.every((opt, idx) => !tentative[idx] || tentative[idx] === opt));
  }

  getProductOptions(product) {
    // Prefer admin-defined swatches from optionsWithValues when available
    if (product.optionsWithValues && Array.isArray(product.optionsWithValues) && product.optionsWithValues.length > 0) {
      return product.optionsWithValues.map(opt => ({
        name: opt.name,
        values: opt.values.map(v => ({
          value: v.value,
          swatchColor: v.swatchColor,
          swatchImage: v.swatchImage
        }))
      }));
    }

    // Fallback to building from variants/options
    const optionsMap = new Map();
    const optionNames = product.options || [];
    product.variants.forEach(variant => {
      variant.options.forEach((optionValue, index) => {
        const optionName = optionNames[index] || `Option ${index + 1}`;
        if (!optionsMap.has(optionName)) {
          optionsMap.set(optionName, new Set());
        }
        optionsMap.get(optionName).add(optionValue);
      });
    });
    return Array.from(optionsMap.entries()).map(([name, values]) => ({ name, values: Array.from(values).map(v => ({ value: v })) }));
  }

  selectVariant(variantSwatch) {
    const optionName = variantSwatch.dataset.optionName;
    const optionValue = variantSwatch.dataset.optionValue;
    const step = this.getCurrentStepType();

    console.log('🎨 Variant selected:', {optionName, optionValue, step});

    // Prevent picking disabled/unavailable option
    if (variantSwatch.hasAttribute('disabled')) {
      this.showNotification('This option is out of stock', 'warning');
      return;
    }

    // Update active state
    const container = variantSwatch.closest('.variant-option-swatches');
    container.querySelectorAll('.variant-option-swatch').forEach(s => s.classList.remove('active'));
    variantSwatch.classList.add('active');

    // Update selected value display
    const selectedValueSpan = container.closest('.variant-option-row')
      ? container.closest('.variant-option-row').querySelector(`[data-selected-value="${optionName}"]`)
      : null;
    if (selectedValueSpan) {
      // Map headrest values to Yes/No for display
      const displayValue = (optionName === 'Headrest')
        ? (optionValue === 'with' ? 'Yes' : 'No')
        : optionValue;
      selectedValueSpan.textContent = displayValue;
    }

    // Update selected item with new variant
    if (this.selectedItems[step]) {
      const product = this.selectedItems[step].product;
      const productSlide = document.querySelector(`[data-step="${step}"].bundle-product-slide.selected`);
      const variantSection = document.querySelector(`[data-step="${this.currentStep}"] [data-variant-section]`);
      const headrestSplit = variantSection && variantSection.getAttribute('data-headrest-split') === 'true';
      const selectedOptions = headrestSplit ? this.getSelectedOptionsForHeadrestSplit(variantSection, product) : this.getSelectedOptions(step);
      
      console.log('📦 Product:', product);
      console.log('🔍 Selected options:', selectedOptions);
      
      // Find matching available variant
      let variant = null;
      if (headrestSplit) {
        const composed = this.composeHeadrestColorValue(selectedOptions.baseColor, selectedOptions.withHeadrest);
        const normComposed = this.normalizeHeadrestOption(composed);
        variant = product.variants.find(v => v.available && this.normalizeHeadrestOption(v.options && v.options[0]) === normComposed) || this.getFirstAvailableVariant(product);
      } else {
        // When some options are not rendered (e.g., only laminate swatches, single leg option),
        // merge currently selected variant options as defaults and allow undefined positions.
        const currentSelected = (this.selectedItems[step] && this.selectedItems[step].selectedOptions) || [];
        const mergedSelection = [];
        const optionCount = Array.isArray(product.options) ? product.options.length : (selectedOptions.length || currentSelected.length);
        for (let i = 0; i < optionCount; i += 1) {
          mergedSelection[i] = selectedOptions[i] || currentSelected[i] || null;
        }

        variant = product.variants.find(v => {
          return v.available && v.options.every((opt, index) => !mergedSelection[index] || opt === mergedSelection[index]);
        }) || this.getFirstAvailableVariant(product);
      }

      console.log('✅ Found variant:', variant);

      if (variant) {
        this.selectedItems[step].variantId = variant.id;
        this.selectedItems[step].price = variant.price;
        this.selectedItems[step].selectedOptions = variant.options;
        // If desk variant changed, align add-on defaults to matching color/laminate if available
        if (step === 'desk') {
          this.syncAddonsDefaultToDesk();
        } else if (step === 'chair') {
          // When chair option changes, immediately sync add-on colour selections
          // and filter colour swatches to the matching chair colour.
          this.syncAddonsDefaultToChair();
          this.syncAddonSingleSectionToChair();
        } else if (step === 'addonSingle') {
          // When add-on variant changes, apply chair image override if provided by metafield
          const override = variant && (variant.image_with_chair || (variant.metafields && variant.metafields.image_with_chair)) || null;
          // try { alert('Add-on image_with_chair: ' + (override || 'null')); } catch (_) {}
          try { console.log('[Bundle][AddonSingle] image_with_chair', override); } catch (_) {}
          this.applyChairAddonImageOverride(override || null);
        }

        // After selecting, recompute disabled states for option buttons
        if (variantSection) {
          if (headrestSplit) {
            this.refreshHeadrestSplitAvailability(variantSection, product);
          } else {
            variantSection.querySelectorAll('.variant-option-row').forEach(row => {
              const optionIndex = parseInt(row.dataset.optionIndex, 10);
              row.querySelectorAll('.variant-option-swatch').forEach(btn => {
                const value = btn.dataset.optionValue;
                const available = this.isOptionCombinationAvailable(product, optionIndex, value);
                if (available) {
                  btn.removeAttribute('disabled');
                  btn.removeAttribute('aria-disabled');
                } else {
                  btn.setAttribute('disabled', 'true');
                  btn.setAttribute('aria-disabled', 'true');
                }
              });
            });
          }
        }

        // Get variant-specific image (prefer metafield override)
        let variantImage = null;
        let allImages = [];
        const primaryOverride = this.getVariantPrimaryImage(variant);
        
        // Try to get images from product data
        if (productSlide) {
          try {
            const productData = JSON.parse(productSlide.dataset.productData);
            
            console.log('📸 Variant featured_image:', variant.featured_image);
            
            if (primaryOverride) {
              variantImage = primaryOverride;
              allImages = [variantImage];
              if (productData.images && productData.images.length > 0) {
                productData.images.forEach(img => {
                  if (img.src && img.src !== variantImage) {
                    allImages.push(img.src);
                  }
                });
              }
              console.log('🖼️ Using variant metafield/primary image:', variantImage);
            } else if (variant.featured_image && (typeof variant.featured_image === 'string' || variant.featured_image.src)) {
              variantImage = typeof variant.featured_image === 'string'
                ? variant.featured_image
                : variant.featured_image.src;
              allImages = [variantImage];
              
              console.log('🖼️ Using variant image:', variantImage);
              
              // Add other product images for thumbnails
              if (productData.images && productData.images.length > 0) {
                productData.images.forEach(img => {
                  if (img.src && img.src !== variantImage) {
                    allImages.push(img.src);
                  }
                });
              }
            } else if (productData.images && productData.images.length > 0) {
              // No variant image: try to infer by color keyword from option value
              const colorKeyword = optionValue || (selectedOptions && selectedOptions[0]) || '';
              const colorMatches = this.findImagesByColor(productData.images, colorKeyword);
              if (colorMatches.length > 0) {
                variantImage = colorMatches[0];
                allImages = [variantImage, ...productData.images.map(img => img.src).filter(src => src !== variantImage)];
                console.log('🎨 Inferred color image from gallery:', variantImage);
              } else {
                // Fallback to all product images
                allImages = productData.images.map(img => img.src);
                variantImage = allImages[0];
                console.log('📷 No variant image, using product images');
              }
            }
          } catch (e) {
            console.log('❌ Error parsing product data:', e);
          }
        }
        
        // Fallback to stored images (try color-aware match first)
        if (!variantImage && this.selectedItems[step].images && this.selectedItems[step].images.length > 0) {
          const colorKeyword = headrestSplit ? (selectedOptions.baseColor || '') : (optionValue || (selectedOptions && selectedOptions[0]) || '');
          const colorMatches = this.findImagesByColor(this.selectedItems[step].images, colorKeyword);
          if (colorMatches.length > 0) {
            variantImage = colorMatches[0];
            allImages = [variantImage, ...this.selectedItems[step].images.filter(src => src !== variantImage)];
            console.log('🎨 Found color image in stored images:', variantImage);
          } else {
            allImages = this.selectedItems[step].images;
            variantImage = allImages[0];
            console.log('💾 Using stored images');
          }
        }

        // Absolute last resort: if variant has a featuredImage at product level, prefer matching by color there too
        if (!variantImage && product && product.featuredImage) {
          variantImage = product.featuredImage;
          allImages = [variantImage, ...(allImages.length ? allImages : [])];
          console.log('📌 Using product.featuredImage as last resort');
        }
        
        console.log('🎯 Final images to update:', {variantImage, allImages, step});
        
        // Update the appropriate image based on step
        if (variantImage) {
          if (step === 'chair') {
            console.log('🪑 Updating chair image');
            this.updateChairImage(allImages, product, variantImage);
          } else if (step === 'desk') {
            console.log('🖥️ Updating desk image');
            this.updateDeskImage(allImages, product, variantImage);
          } else if (step === 'addonSingle') {
            // Update the selected add-on single tile image to variant metafield/featured
            const tileImg = productSlide && productSlide.querySelector('.slide-image img');
            if (tileImg) {
              const newSrc = this.getImageUrl(variantImage, 150);
              tileImg.style.opacity = '0';
              setTimeout(() => {
                tileImg.src = newSrc;
                tileImg.style.opacity = '1';
              }, 120);
            }
            // Persist images for sidebar/summary consistency
            this.selectedItems.addonSingle.images = allImages;
            this.selectedItems.addonSingle.selectedOptions = variant.options;
          }
          
          // Update thumbnails with variant images
          this.updateThumbnails(allImages, step);
          
          // Update thumbnail active state to match variant image
          this.updateThumbnailActiveState(variantImage, step);

          // Also update the selected product tile image (within the slider)
          if (productSlide) {
            const tileImg = productSlide.querySelector('.slide-image img');
            if (tileImg) {
              const newSrc = this.getImageUrl(variantImage, 150);
              tileImg.style.opacity = '0';
              setTimeout(() => {
                tileImg.src = newSrc;
                tileImg.style.opacity = '1';
              }, 120);
            }
          }
        } else {
          console.warn('⚠️ No variant image found!');
        }

        this.updateAllPricing();
        this.updateSidebar();
      } else {
        console.warn('⚠️ No matching variant found!');
      }
    } else {
      console.warn('⚠️ No selected item for step:', step);
    }
  }

  getDeskColorLikeSelectedValue() {
    if (!this.selectedItems.desk || !this.selectedItems.desk.product) return null;
    const product = this.selectedItems.desk.product;
    const names = (product.optionsWithValues || product.options || []).map(o => (typeof o === 'string' ? o : o.name));
    const selected = this.selectedItems.desk.selectedOptions || [];
    let idx = -1;
    if (Array.isArray(names) && names.length) {
      names.forEach((name, i) => {
        const lower = (name || '').toLowerCase();
        if (idx === -1 && (/(color|colour|couleur|colore|farbe|kleur|finish|laminate)/.test(lower))) idx = i;
      });
    }
    if (idx >= 0 && selected[idx]) return selected[idx];
    return null;
  }

  syncAddonsDefaultToDesk() {
    const matchValue = this.getDeskColorLikeSelectedValue();
    if (!matchValue) return;
    document.querySelectorAll('.bundle-addon-item').forEach(addonItem => {
      // For each swatch group, if a value matches matchValue, set it active
      const groups = addonItem.querySelectorAll('.addon-variant-swatches');
      if (!groups || groups.length === 0) return;
      groups.forEach(g => {
        const matchBtn = Array.from(g.querySelectorAll('.addon-variant-swatch')).find(btn => {
          if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return false;
          return (btn.dataset.optionValue || '').toLowerCase() === (matchValue || '').toLowerCase();
        });
        if (matchBtn) this.onAddonSwatchClick(matchBtn);
      });
    });
  }

  updateImageForVariant(variantImage) {
    const mainImage = document.querySelector('[data-main-image]');
    mainImage.style.opacity = '0';
    
    setTimeout(() => {
      mainImage.src = this.getImageUrl(variantImage, 800);
      mainImage.style.opacity = '1';
    }, 150);
  }

  getSelectedOptions(step) {
    const options = [];
    const variantSection = document.querySelector(`[data-step="${this.currentStep}"] [data-variant-section]`);
    
    if (variantSection) {
      variantSection.querySelectorAll('.variant-option-row').forEach(row => {
        const activeBtn = row.querySelector('.variant-option-swatch.active');
        if (activeBtn) {
          options.push(activeBtn.dataset.optionValue);
        }
      });
    }
    
    return options;
  }

  getColorValue(colorName) {
    const colorMap = {
      'black': '#000000',
      'white': '#FFFFFF',
      'gray': '#808080',
      'grey': '#808080',
      'charcoal': '#3a3a3a',
      'graphite': '#4b4b4b',
      'silver': '#c0c0c0',
      'cloud': '#e8eef2',
      'cinder': '#606060',
      'oat': '#d9c7a1',
      'walnut': '#7b5a3e',
      'oak': '#c69c6d',
      'whiteoak': '#e6d2b5',
      'maple': '#e5c080',
      'birch': '#eadfbd',
      'elm': '#b88a5a',
      'autumnelm': '#b88a5a',
      'ash': '#b2b2b2',
      'woodgrain': '#c1a27b',
      'vapor': '#dcdcdc',
      'sage': '#91a48b',
      'galaxy': '#0b1a2d',
      'blue': '#0000FF',
      'red': '#FF0000',
      'green': '#008000',
      'yellow': '#FFFF00',
      'orange': '#FFA500',
      'purple': '#800080',
      'pink': '#FFC0CB',
      'brown': '#A52A2A',
      'beige': '#F5F5DC',
      'navy': '#000080',
      'teal': '#008080'
    };

    const normalized = (colorName || '').toLowerCase().replace(/[^a-z]/g, '');
    return colorMap[normalized] || '#CCCCCC';
  }

  // Attempts to derive a representative color from arbitrary option text
  guessColorFromText(value) {
    if (!value) return '#CCCCCC';
    // If value already looks like a CSS color (#fff or rgb), return as-is
    const raw = value.toString().trim();
    if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(raw)) return raw;
    // Try tokenized matching (handles bundled strings like "White - Charcoal")
    const tokens = raw
      .toLowerCase()
      .replace(/[()]/g, ' ')
      .split(/\s|\/|\-|,|\+/)
      .filter(Boolean);
    for (const token of tokens) {
      const hex = this.getColorValue(token);
      if (hex && hex !== '#CCCCCC') return hex;
    }
    // Fallback to full phrase mapping (e.g., Autumn Elm)
    const phraseHex = this.getColorValue(raw);
    if (phraseHex && phraseHex !== '#CCCCCC') return phraseHex;
    return '#CCCCCC';
  }

  // --- Headrest split helpers ---
  hasHeadrestMergedInColor(product) {
    try {
      const options = (product && product.optionsWithValues && product.optionsWithValues.length > 0)
        ? [{
            name: product.optionsWithValues[0].name,
            values: product.optionsWithValues[0].values.map(v => ({ value: v.value }))
          }]
        : this.getProductOptions(product);
      if (!options || options.length !== 1) return false;
      const values = (options[0].values || []).map(v => (typeof v === 'string' ? v : v.value) || '');
      return values.some(v => /head\s*-?\s*rest/i.test(v) || /headrest/i.test(v));
    } catch (_) { return false; }
  }

  parseHeadrestColorValue(value) {
    const raw = (value || '').toString();
    const withHeadrest = /head\s*-?\s*rest/i.test(raw) || /headrest/i.test(raw) || /with\s+headrest/i.test(raw);
    // Remove headrest annotations and punctuation
    let base = raw.replace(/\(.*?\)/g, ' ')
      .replace(/with\s*headrest|head\s*-?\s*rest|headrest/gi, ' ')
      .replace(/[-_/]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return { base, withHeadrest };
  }

  composeHeadrestColorValue(base, withHeadrest) {
    const b = (base || '').toString().trim();
    return withHeadrest ? `${b}-Headrest` : b;
  }

  normalizeHeadrestOption(value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .replace(/\(.*?\)/g, '')
      .replace(/with\s*headrest/gi, 'headrest')
      .replace(/\s+/g, '')
      .replace(/[_-]+/g, '-')
      .trim();
  }

  getSelectedOptionsForHeadrestSplit(variantSection, product) {
    const colorActive = variantSection.querySelector('.variant-option-row[data-row-type="color"] .variant-option-swatch.active');
    const headrestActive = variantSection.querySelector('.variant-option-row[data-row-type="headrest"] .variant-option-swatch.active');
    const baseColor = colorActive ? colorActive.dataset.optionValue : (this.selectedItems.chair && this.selectedItems.chair.selectedOptions && this.parseHeadrestColorValue(this.selectedItems.chair.selectedOptions[0]).base) || '';
    const withHeadrest = headrestActive ? (headrestActive.dataset.optionValue === 'with') : false;
    return { baseColor, withHeadrest };
  }

  refreshHeadrestSplitAvailability(variantSection, product) {
    if (!variantSection || !product) return;
    const sel = this.getSelectedOptionsForHeadrestSplit(variantSection, product);
    // Update color row based on current headrest flag
    variantSection.querySelectorAll('.variant-option-row[data-row-type="color"] .variant-option-swatch').forEach(btn => {
      const composed = this.composeHeadrestColorValue(btn.dataset.optionValue, sel.withHeadrest);
      const available = product.variants.some(v => v.available && this.normalizeHeadrestOption(v.options && v.options[0]) === this.normalizeHeadrestOption(composed));
      if (available) {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
      } else {
        btn.setAttribute('disabled', 'true');
        btn.setAttribute('aria-disabled', 'true');
      }
    });
    // Update headrest row based on current color selection
    const headrestBtns = variantSection.querySelectorAll('.variant-option-row[data-row-type="headrest"] .variant-option-swatch');
    headrestBtns.forEach(btn => {
      const withHeadrest = btn.dataset.optionValue === 'with';
      const composed = this.composeHeadrestColorValue(sel.baseColor, withHeadrest);
      const available = product.variants.some(v => v.available && this.normalizeHeadrestOption(v.options && v.options[0]) === this.normalizeHeadrestOption(composed));
      if (available) {
        btn.removeAttribute('disabled');
        btn.removeAttribute('aria-disabled');
      } else {
        btn.setAttribute('disabled', 'true');
        btn.setAttribute('aria-disabled', 'true');
      }
    });
  }

  // Slider Controls
  slideNext(button) {
    const slider = button.closest('[data-products-slider]');
    const track = slider.querySelector('[data-slider-track]');
    const slides = track.querySelectorAll('.bundle-product-slide');
    const container = slider.querySelector('[data-slider-container]');
    const slideWidth = slides[0].offsetWidth + 16; // including gap
    const visibleSlides = Math.floor(container.offsetWidth / slideWidth);
    
    if (this.currentSlideIndex < slides.length - visibleSlides) {
      this.currentSlideIndex++;
      track.style.transform = `translateX(-${this.currentSlideIndex * slideWidth}px)`;
      this.updateSliderButtons(slider);
    }
  }

  slidePrev(button) {
    const slider = button.closest('[data-products-slider]');
    const track = slider.querySelector('[data-slider-track]');
    const slides = track.querySelectorAll('.bundle-product-slide');
    const slideWidth = slides[0].offsetWidth + 16;
    
    if (this.currentSlideIndex > 0) {
      this.currentSlideIndex--;
      track.style.transform = `translateX(-${this.currentSlideIndex * slideWidth}px)`;
      this.updateSliderButtons(slider);
    }
  }

  updateSliderButtons(slider) {
    const track = slider.querySelector('[data-slider-track]');
    const slides = track.querySelectorAll('.bundle-product-slide');
    const container = slider.querySelector('[data-slider-container]');
    const slideWidth = slides[0] ? slides[0].offsetWidth + 16 : 0;
    const visibleSlides = Math.floor(container.offsetWidth / slideWidth);
    
    const prevBtn = slider.querySelector('[data-slider-prev]');
    const nextBtn = slider.querySelector('[data-slider-next]');
    
    // Hide nav buttons entirely when there is nothing to slide
    const needsNav = slides.length > visibleSlides && slideWidth > 0;
    if (prevBtn) {
      prevBtn.style.display = needsNav ? '' : 'none';
      prevBtn.disabled = this.currentSlideIndex === 0;
      prevBtn.setAttribute('aria-hidden', needsNav ? 'false' : 'true');
    }
    if (nextBtn) {
      nextBtn.style.display = needsNav ? '' : 'none';
      nextBtn.disabled = this.currentSlideIndex >= slides.length - visibleSlides;
      nextBtn.setAttribute('aria-hidden', needsNav ? 'false' : 'true');
    }
    // If nav not needed, snap back to start to avoid stray offset
    if (!needsNav) {
      this.currentSlideIndex = 0;
      if (track) track.style.transform = 'translateX(0px)';
    }
  }

  // Add-ons
  toggleAddon(checkbox) {
    const addonItem = checkbox.closest('.bundle-addon-item');
    const addonId = addonItem.dataset.addonId;
    const product = this.getAddonProductById(addonId);

    // Prevent selecting OOS add-ons
    if (!this.productHasAvailableVariant(product)) {
      checkbox.checked = false;
      this.showNotification('This add-on is out of stock', 'warning');
      return;
    }
    
    if (checkbox.checked) {
      const addonData = {
        id: addonId,
        handle: addonItem.dataset.addonHandle,
        title: addonItem.dataset.addonTitle,
        price: parseFloat(addonItem.dataset.addonPrice),
        variantId: this.getAddonVariantId(addonItem),
        selectedOptions: this.getAddonSelectedOptions(addonItem)
      };
      
      // Add or update selection
      const existingIdx = this.selectedItems.addons.findIndex(a => a.id == addonId);
      if (existingIdx > -1) {
        this.selectedItems.addons[existingIdx] = addonData;
      } else {
      this.selectedItems.addons.push(addonData);
      }
      addonItem.classList.add('selected');
    } else {
      this.selectedItems.addons = this.selectedItems.addons.filter(
        addon => addon.id !== addonId
      );
      addonItem.classList.remove('selected');
    }
    
    this.updateAllPricing();
    this.updateSidebar();
  }

  getAddonVariantId(addonItem) {
    const addonId = addonItem.dataset.addonId;
    const products = this.bundleData.products.addons;
    const product = products.find(p => p.id == addonId);
    if (!product) return null;
    // Build selected options from selects
    const selectedOptions = this.getAddonSelectedOptions(addonItem);
    // Find matching variant by options
    const matched = product.variants.find(v => v.options.every((opt, idx) => opt === selectedOptions[idx])) || product.variants[0];
    return matched ? matched.id : null;
  }

  getAddonSelectedOptions(addonItem) {
    const selectedOptions = [];
    // Prefer swatch groups
    const groups = addonItem.querySelectorAll('.addon-variant-swatches');
    if (groups && groups.length > 0) {
      groups.forEach(g => {
        const active = g.querySelector('.addon-variant-swatch.active');
        if (active) selectedOptions.push(active.dataset.optionValue);
      });
      return selectedOptions;
    }
    // Fallback to selects
    addonItem.querySelectorAll('.addon-variant-select').forEach((sel) => selectedOptions.push(sel.value));
    return selectedOptions;
  }

  onAddonVariantChange(selectEl) {
    const addonItem = selectEl.closest('.bundle-addon-item');
    if (!addonItem) return;
    const addonId = addonItem.dataset.addonId;
    const product = this.bundleData.products.addons.find(p => p.id == addonId);
    if (!product) return;

    // Find matching variant
    const selectedOptions = [];
    addonItem.querySelectorAll('.addon-variant-select').forEach((sel) => selectedOptions.push(sel.value));
    const variant = product.variants.find(v => v.options.every((opt, idx) => opt === selectedOptions[idx])) || null;

    // Update preview image within the add-on card
    const imgEl = addonItem.querySelector('.addon-image img');
    if (imgEl) {
      let src = null;
      const primaryOverride = this.getVariantPrimaryImage(variant);
      if (primaryOverride) {
        src = primaryOverride;
      } else if (variant && variant.featured_image && (variant.featured_image.src || typeof variant.featured_image === 'string')) {
        src = typeof variant.featured_image === 'string' ? variant.featured_image : variant.featured_image.src;
      }
      if (!src && product.featuredImage) src = product.featuredImage;
      if (src) {
        imgEl.style.opacity = '0';
        setTimeout(() => {
          imgEl.src = this.getImageUrl(src, 200);
          imgEl.style.opacity = '1';
        }, 120);
      }
    }

    // If this add-on is selected, update stored variantId and price
    const idx = this.selectedItems.addons.findIndex(a => a.id == addonId);
    if (idx > -1 && variant) {
      this.selectedItems.addons[idx].variantId = variant.id;
      this.selectedItems.addons[idx].price = variant.price;
      this.updateAllPricing();
      this.updateSidebar();
    }
  }

  onAddonSwatchClick(swatch) {
    const group = swatch.closest('.addon-variant-swatches');
    if (!group) return;
    // Toggle active state within the group
    group.querySelectorAll('.addon-variant-swatch').forEach(btn => btn.classList.remove('active'));
    swatch.classList.add('active');

    // Update corresponding hidden selection (reuse logic): build temp selects from swatches
    const addonItem = swatch.closest('.bundle-addon-item');
    if (!addonItem) return;

    // Build selected options from current active swatches across groups
    const selectedOptions = [];
    addonItem.querySelectorAll('.addon-variant-swatches').forEach(g => {
      const active = g.querySelector('.addon-variant-swatch.active');
      if (active) selectedOptions.push(active.dataset.optionValue);
    });

    const addonId = addonItem.dataset.addonId;
    const product = this.bundleData.products.addons.find(p => p.id == addonId);
    if (!product) return;
    const variant = product.variants.find(v => v.options.every((opt, idx) => opt === selectedOptions[idx])) || null;

    // Update selected value label for this option
    const optionName = swatch.dataset.optionName;
    const labelTarget = addonItem.querySelector(`[data-addon-selected-value="${optionName}"]`);
    if (labelTarget) {
      const optionName = swatch.dataset.optionName;
      const raw = swatch.dataset.optionValue;
      labelTarget.textContent = (optionName === 'Headrest') ? (raw === 'with' ? 'Yes' : 'No') : raw;
    }

    // Update image
    const imgEl = addonItem.querySelector('.addon-image img');
    if (imgEl) {
      let src = null;
      const primaryOverride = this.getVariantPrimaryImage(variant);
      if (primaryOverride) {
        src = primaryOverride;
      } else if (variant && variant.featured_image && (variant.featured_image.src || typeof variant.featured_image === 'string')) {
        src = typeof variant.featured_image === 'string' ? variant.featured_image : variant.featured_image.src;
      }
      if (!src && product.featuredImage) src = product.featuredImage;
      if (src) {
        imgEl.style.opacity = '0';
        setTimeout(() => {
          imgEl.src = this.getImageUrl(src, 200);
          imgEl.style.opacity = '1';
        }, 120);
      }
    }

    // If selected, update variantId/price/options
    const idx = this.selectedItems.addons.findIndex(a => a.id == addonId);
    if (idx > -1 && variant) {
      this.selectedItems.addons[idx].variantId = variant.id;
      this.selectedItems.addons[idx].price = variant.price;
      this.selectedItems.addons[idx].selectedOptions = selectedOptions;
      this.updateAllPricing();
      this.updateSidebar();
    }

    // Recompute disabled states for this add-on
    this.refreshAddonAvailability(addonItem, product);
  }

  // --- Add-on availability helpers ---
  refreshAddonAvailability(addonItem, product) {
    if (!addonItem || !product) return;
    // Build base selection from current active swatches
    const base = [];
    addonItem.querySelectorAll('.addon-variant-swatches').forEach((g, i) => {
      const active = g.querySelector('.addon-variant-swatch.active');
      base[i] = active ? active.dataset.optionValue : null;
    });
    addonItem.querySelectorAll('.addon-variant-swatches').forEach((g, optionIndex) => {
      g.querySelectorAll('.addon-variant-swatch').forEach(btn => {
        const value = btn.dataset.optionValue;
        const available = this.isAddonOptionAvailable(product, base, optionIndex, value);
        if (available) {
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
        } else {
          btn.setAttribute('disabled', 'true');
          btn.setAttribute('aria-disabled', 'true');
        }
      });
    });
  }

  isAddonOptionAvailable(product, baseSelection, changedOptionIndex, chosenValue) {
    if (!product || !product.variants) return false;
    const tentative = Array.from(baseSelection || []);
    tentative[changedOptionIndex] = chosenValue;
    return product.variants.some(v => v.available && v.options.every((opt, idx) => !tentative[idx] || tentative[idx] === opt));
  }

  // Navigation
  navigateNext() {
    const currentStepType = this.getCurrentStepType();
    
    if (currentStepType === 'chair' && !this.selectedItems.chair) {
      this.showNotification('Please select a chair to continue', 'warning');
      return;
    }
    
    if (currentStepType === 'desk' && !this.selectedItems.desk) {
      this.showNotification('Please select a desk to continue', 'warning');
      return;
    }
    const nextStep = this.getNextAvailableStep(this.currentStep);
    if (nextStep !== this.currentStep) {
      this.currentStep = nextStep;
      this.currentSlideIndex = 0;
      this.showStep(this.currentStep);
      // Auto-select first AVAILABLE product in new step (chair/desk only)
      if (this.currentStep <= 2) {
        const stepEl = document.querySelector(`[data-step="${this.currentStep}"]`);
        const stepType = stepEl ? stepEl.dataset.stepType : '';
        const slides = Array.from(stepEl ? stepEl.querySelectorAll('.bundle-product-slide') : []);
        const firstAvailable = slides.find(slide => {
          const product = this.getProductByIdAndStep(slide.dataset.productId, stepType);
          return this.productHasAvailableVariant(product);
        });
        if (firstAvailable) this.selectProduct(firstAvailable);
      }
    }
  }

  navigateBack() {
    const prevStep = this.getPrevAvailableStep(this.currentStep);
    if (prevStep !== this.currentStep) {
      this.currentStep = prevStep;
      this.currentSlideIndex = 0;
      this.showStep(this.currentStep);
    }
  }

  goToSummary() {
    // Jump to the last available step (summary)
    if (!this.availableSteps || this.availableSteps.length === 0) this.refreshDynamicSteps();
    this.currentStep = (this.availableSteps && this.availableSteps[this.availableSteps.length - 1]) || 5;
    this.showStep(this.currentStep);
    this.updateSummary();
  }

  getCurrentStepType() {
    const stepEl = document.querySelector(`[data-step="${this.currentStep}"]`);
    return stepEl ? stepEl.dataset.stepType : '';
  }

  showStep(stepNumber) {
    // Ensure we skip disabled steps
    if (!this.availableSteps || this.availableSteps.length === 0) this.refreshDynamicSteps();
    if (this.availableSteps && !this.availableSteps.includes(stepNumber)) {
      const next = this.getNextAvailableStep(stepNumber);
      if (next !== stepNumber) return this.showStep(next);
      const prev = this.getPrevAvailableStep(stepNumber);
      if (prev !== stepNumber) return this.showStep(prev);
      return; // nothing to show
    }
    document.querySelectorAll('.bundle-step').forEach(step => {
      step.style.display = 'none';
    });
    
    const currentStep = document.querySelector(`[data-step="${stepNumber}"]`);
    if (currentStep) {
      currentStep.style.display = 'block';
      // Reset slider position and controls for this step
      const slider = currentStep.querySelector('[data-products-slider]');
      if (slider) {
        const track = slider.querySelector('[data-slider-track]');
        this.currentSlideIndex = 0;
        if (track) track.style.transform = 'translateX(0px)';
        this.updateSliderButtons(slider);
      }

      // Ensure no pre-rendered selection remains; reapply if we truly have a selected item
      const stepType = currentStep.dataset.stepType;
      currentStep.querySelectorAll('.bundle-product-slide').forEach(slide => {
        slide.classList.remove('selected');
      });
      const selected = stepType && this.selectedItems[stepType];
      if (selected && selected.id) {
        const selectedSlide = currentStep.querySelector(`.bundle-product-slide[data-product-id="${selected.id}"]`);
        if (selectedSlide && !selectedSlide.classList.contains('out-of-stock') && selectedSlide.getAttribute('aria-disabled') !== 'true') {
          selectedSlide.classList.add('selected');
        }
        // Build variant UI for this step from current selection
        if (selected.product) {
          this.updateVariantSection(selected.product, stepType);
        }
      }

      // Apply chair-based filtering when entering the add-on (single) step
      if (currentStep.dataset.stepType === 'addonSingle') {
        this.applyAddonSingleFilter();
        // If options have not yet been built (first visit), render from first visible tile
        try {
          const variantSection = currentStep.querySelector('[data-variant-section]');
          const hasOptions = !!(variantSection && variantSection.querySelector('[data-variant-options]') && variantSection.querySelector('[data-variant-options]').children.length);
          if (!hasOptions) {
            this.renderAddonSingleDefaultOptions();
          }
        } catch (_) {}
        this.syncAddonSingleSectionToChair(currentStep);
      }
      // Apply chair-based defaults to multi add-ons when entering that step as well
      if (currentStep.dataset.stepType === 'addons') {
        this.syncAddonsDefaultToChair();
      }
    }
    
    document.querySelectorAll('[data-nav-back]').forEach(btn => {
      btn.disabled = stepNumber === 1;
    });
    
    // Update progress bar
    this.updateProgressBar(stepNumber);
    
    // Smooth scroll into the current step section for better context
    // if (currentStep && typeof currentStep.getBoundingClientRect === 'function') {
    //   const rect = currentStep.getBoundingClientRect();
    //   const y = rect.top + window.pageYOffset - 20;
    //   window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
    // }
  }

  getNextAvailableStep(fromStep) {
    if (!this.availableSteps || this.availableSteps.length === 0) this.refreshDynamicSteps();
    const idx = this.availableSteps.indexOf(fromStep);
    if (idx === -1) return fromStep;
    return this.availableSteps[Math.min(idx + 1, this.availableSteps.length - 1)] || fromStep;
  }

  getPrevAvailableStep(fromStep) {
    if (!this.availableSteps || this.availableSteps.length === 0) this.refreshDynamicSteps();
    const idx = this.availableSteps.indexOf(fromStep);
    if (idx === -1) return fromStep;
    return this.availableSteps[Math.max(idx - 1, 0)] || fromStep;
  }

  getChairColorLikeSelectedValue() {
    if (!this.selectedItems.chair || !this.selectedItems.chair.product) return null;
    const product = this.selectedItems.chair.product;
    const names = (product.optionsWithValues || product.options || []).map(o => (typeof o === 'string' ? o : o.name));
    const selected = this.selectedItems.chair.selectedOptions || [];
    let idx = -1;
    if (Array.isArray(names) && names.length) {
      names.forEach((name, i) => {
        const lower = (name || '').toLowerCase();
        if (idx === -1 && (/(color|colour|couleur|colore|farbe|kleur|finish|laminate|fabric|upholstery)/.test(lower))) idx = i;
      });
    }
    if (idx >= 0 && selected[idx]) return selected[idx];
    return null;
  }

  syncAddonsDefaultToChair() {
    const matchValue = this.getChairColorLikeSelectedValue();
    if (!matchValue) return;
    const matchLower = String(matchValue || '').toLowerCase();
    document.querySelectorAll('.bundle-addon-item').forEach((addonItem) => {
      const groups = addonItem.querySelectorAll('.addon-variant-swatches');
      if (!groups || groups.length === 0) return;
      groups.forEach((g) => {
        const optionName = (g.getAttribute('data-addon-option') || '').toLowerCase();
        const isColorLike = /(color|colour|couleur|colore|farbe|kleur|finish|laminate)/.test(optionName);
        const swatches = Array.from(g.querySelectorAll('.addon-variant-swatch'));
        if (!swatches.length) return;
        // Always clear previous filtering
        swatches.forEach((btn) => { btn.style.display = ''; btn.removeAttribute('aria-hidden'); });
        const matchBtn = swatches.find((btn) => {
          if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return false;
          return (btn.dataset.optionValue || '').toLowerCase() === matchLower;
        });
        // For multi add-ons: do NOT hide non-matching options. Only auto-activate the matching colour if present.
        if (matchBtn) this.onAddonSwatchClick(matchBtn);
      });
    });
  }

  // Try to align the Add-on (single) variant swatches to the selected chair color
  syncAddonSingleSectionToChair(scopeEl = null) {
    const matchValue = this.getChairColorLikeSelectedValue();
    if (!matchValue) return;
    const stepEl = scopeEl || document.querySelector('[data-step-type="addonSingle"]');
    if (!stepEl) return;
    const variantSection = stepEl.querySelector('[data-variant-section]');
    if (!variantSection) return;
    // Try to use selected product data when available; otherwise operate UI-only
    const hasSelectedAddon = !!(this.selectedItems.addonSingle && this.selectedItems.addonSingle.product);
    const product = hasSelectedAddon ? this.selectedItems.addonSingle.product : null;

    // For each swatch group in the addonSingle variant section, activate matching value when found
    variantSection.querySelectorAll('.variant-option-row').forEach(row => {
      const swatches = row.querySelectorAll('.variant-option-swatch');
      if (!swatches || !swatches.length) return;
      // Determine the option name from the row or its swatch group
      const groupEl = row.querySelector('.variant-option-swatches');
      const optionNameSource = (row.dataset.optionName || row.getAttribute('data-option-name') || groupEl?.getAttribute('data-option-name') || '');
      const isColorLike = /(color|colour|couleur|colore|farbe|kleur|finish|laminate)/.test(optionNameSource.toLowerCase());
      const matchBtn = Array.from(swatches).find(btn => (btn.dataset.optionValue || '').toLowerCase() === (matchValue || '').toLowerCase());
      // If colour-like, hide non-matching options
      if (isColorLike) {
        Array.from(swatches).forEach(btn => {
          const ok = (btn.dataset.optionValue || '').toLowerCase() === (matchValue || '').toLowerCase();
          btn.style.display = ok ? '' : 'none';
          if (!ok) btn.setAttribute('aria-hidden', 'true'); else btn.removeAttribute('aria-hidden');
        });
      }
      if (matchBtn) {
        // Toggle active state
        const group = matchBtn.closest('.variant-option-swatches');
        if (group) group.querySelectorAll('.variant-option-swatch').forEach(b => b.classList.remove('active'));
        matchBtn.classList.add('active');
        // Update inline label
        const optionName = matchBtn.dataset.optionName;
        const label = row.querySelector(`[data-selected-value="${optionName}"]`);
        if (label) label.textContent = matchBtn.dataset.optionValue;
      }
    });

    // Recompute selected options from the visible UI
    const selectedOptions = [];
    variantSection.querySelectorAll('.variant-option-row').forEach(row => {
      const active = row.querySelector('.variant-option-swatch.active');
      if (active) selectedOptions.push(active.dataset.optionValue);
    });
    // Resolve a product context even if none selected (use first visible slide)
    let ctxProduct = product;
    if (!ctxProduct) {
      try {
        const slides = Array.from(stepEl.querySelectorAll('.bundle-product-slide[data-step="addonSingle"]'));
        const firstVisible = slides.find(slide => slide.style.display !== 'none') || slides[0];
        if (firstVisible) {
          const productId = firstVisible.dataset.productId;
          ctxProduct = (this.bundleData?.products?.addonSingles || []).find(p => p.id == productId) || JSON.parse(firstVisible.dataset.productData || '{}');
        }
      } catch (_) {}
    }
    if (ctxProduct && Array.isArray(ctxProduct.variants)) {
      const vForImage = ctxProduct.variants.find(v => v.available && v.options.every((opt, idx) => opt === selectedOptions[idx])) || (ctxProduct.variants.find(v => v.available) || ctxProduct.variants[0] || null);
      // Update the tile preview image even if not selected
      try {
        const selectedSlide = stepEl.querySelector('.bundle-product-slide.selected');
        const targetSlide = selectedSlide || (function () {
          const slides = Array.from(stepEl.querySelectorAll('.bundle-product-slide[data-step="addonSingle"]'));
          return slides.find(slide => slide.style.display !== 'none') || slides[0] || null;
        })();
        if (targetSlide && vForImage) {
          const tileImg = targetSlide.querySelector('.slide-image img');
          const override = this.getVariantPrimaryImage(vForImage);
          const src = override || (typeof vForImage.featured_image === 'string' ? vForImage.featured_image : (vForImage.featured_image?.src || null));
          if (tileImg && src) {
            tileImg.style.opacity = '0';
            setTimeout(() => {
              tileImg.src = this.getImageUrl(src, 150);
              tileImg.style.opacity = '1';
            }, 120);
          }
        }
      } catch (_) {}
    }
    // If the add-on is selected, also update stored selection/pricing and availability states
    if (hasSelectedAddon && product) {
      const variant = product.variants.find(v => v.available && v.options.every((opt, idx) => opt === selectedOptions[idx])) || null;
      if (variant) {
        this.selectedItems.addonSingle.variantId = variant.id;
        this.selectedItems.addonSingle.price = variant.price;
        this.selectedItems.addonSingle.selectedOptions = variant.options;
        // Apply or clear chair override based on metafield
        const override = variant && (variant.image_with_chair || (variant.metafields && variant.metafields.image_with_chair)) || null;
        // try { alert('Add-on image_with_chair: ' + (override || 'null')); } catch (_) {}
        try { console.log('[Bundle][AddonSingle][Sync] image_with_chair', override); } catch (_) {}
        this.applyChairAddonImageOverride(override || null);
        // Recompute disabled states for this variant section according to availability
        variantSection.querySelectorAll('.variant-option-row').forEach((row, optionIndex) => {
          row.querySelectorAll('.variant-option-swatch').forEach(btn => {
            const value = btn.dataset.optionValue;
            const available = this.isCombinationAvailableWithBase(product, selectedOptions, optionIndex, value);
            if (available) {
              btn.removeAttribute('disabled');
              btn.removeAttribute('aria-disabled');
            } else {
              btn.setAttribute('disabled', 'true');
              btn.setAttribute('aria-disabled', 'true');
            }
          });
        });
        this.updateAllPricing();
        this.updateSidebar();
      }
    }
  }

  // Filter Add-on (single) items to those matching the selected chair title keyword
  applyAddonSingleFilter() {
    try {
      const stepEl = document.querySelector('[data-step-type="addonSingle"]');
      if (!stepEl) return;

      const chairTitle = this.selectedItems && this.selectedItems.chair && this.selectedItems.chair.title
        ? String(this.selectedItems.chair.title)
        : '';
      const keyword = this.extractModelKeywordFromTitle(chairTitle);

      const slides = Array.from(stepEl.querySelectorAll('.bundle-product-slide[data-step="addonSingle"]'));
      if (slides.length === 0) return;

      // Ensure message container exists (and position it ABOVE the slider for better visibility)
      let msgEl = stepEl.querySelector('[data-addon-single-filter-msg]');
      if (!msgEl) {
        msgEl = document.createElement('div');
        msgEl.setAttribute('data-addon-single-filter-msg', '');
        msgEl.className = 'addon-single-filter-message';
        msgEl.style.display = 'none';
        // Insert before the products slider (right under the step header)
        const sliderAnchor = stepEl.querySelector('[data-products-slider]');
        if (sliderAnchor && sliderAnchor.parentNode === stepEl) {
          stepEl.insertBefore(msgEl, sliderAnchor);
        } else {
          // Fallback: place just after header if present, otherwise append
          const header = stepEl.querySelector('.bundle-step-header');
          if (header && header.parentNode === stepEl) {
            header.insertAdjacentElement('afterend', msgEl);
          } else {
        stepEl.appendChild(msgEl);
          }
        }
        // Small spacing
        msgEl.style.margin = '6px 0 10px';
      }

      // If no keyword (no chair yet), show all and hide message
      if (!keyword) {
        slides.forEach(slide => { slide.style.display = ''; });
        msgEl.style.display = 'none';
        // Ensure variant section is visible when there is no filter
        const vs = stepEl.querySelector('[data-variant-section]');
        if (vs) vs.style.display = '';
        return;
      }

      let matches = 0;
      slides.forEach(slide => {
        const title = (slide.dataset.productTitle || '').toLowerCase();
        const isMatch = title.includes(keyword);
        slide.style.display = isMatch ? '' : 'none';
        if (isMatch) matches++;
      });

      // Toggle the variant options panel visibility based on matches
      const variantSection = stepEl.querySelector('[data-variant-section]');
      if (variantSection) {
        variantSection.style.display = matches === 0 ? 'none' : '';
      }

      // If current selection is hidden, clear the add-on single selection
      const selectedSlide = stepEl.querySelector('.bundle-product-slide.selected');
      if (selectedSlide && selectedSlide.style.display === 'none') {
        selectedSlide.classList.remove('selected');
        this.selectedItems.addonSingle = null;
        if (variantSection) {
          const desc = variantSection.querySelector('[data-product-description]');
          const opts = variantSection.querySelector('[data-variant-options]');
          if (desc) desc.textContent = '';
          if (opts) opts.innerHTML = '';
        }
        this.updateAllPricing();
        this.updateSidebar();
      }

      if (matches === 0) {
        msgEl.textContent = 'No matching add-on found for your selected chair.';
        msgEl.style.display = 'block';
      } else {
        msgEl.style.display = 'none';
      }

      // Refresh slider buttons after visibility changes
      const slider = stepEl.querySelector('[data-products-slider]');
      if (slider) this.updateSliderButtons(slider);
    } catch (e) {
      console.warn('applyAddonSingleFilter error:', e);
    }
  }

  extractModelKeywordFromTitle(title) {
    if (!title || typeof title !== 'string') return null;
    const stop = new Set(['the','and','with','chair','desk','table','addon','add-on','accessory','accessories','bundle','collection','by','of','for','in','on','set','single','optional']);
    const words = title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, ' ')
      .split(/\s+/)
      .filter(w => w && w.length >= 3 && !stop.has(w));
    return words[0] || null;
  }

  updateProgressBar(currentStep) {
    // Use only visible steps for state and fill
    const all = Array.from(document.querySelectorAll('.progress-step'));
    const visible = all.filter(el => el.style.display !== 'none');
    const currentIndex = (this.availableSteps || []).indexOf(currentStep);
    visible.forEach((stepEl, visIndex) => {
      const circle = stepEl.querySelector('.progress-circle');
      if (!circle) return;
      circle.classList.remove('active', 'completed');
      if (visIndex < currentIndex) circle.classList.add('completed');
      if (visIndex === currentIndex) circle.classList.add('active');
    });

    const stepper = document.querySelector('.bundle-progress-stepper');
    if (stepper) {
      const segments = Math.max(1, visible.length - 1);
      const fillPercent = Math.max(0, Math.min(100, (currentIndex / segments) * 100));
      stepper.style.setProperty('--progress-fill', fillPercent + '%');
    }
  }

  // Pricing
  updateAllPricing() {
    const pricing = this.calculatePricing();
    
    this.updateElement('[data-original-price]', this.formatMoney(pricing.subtotal));
    this.updateElement('[data-current-price]', this.formatMoney(pricing.total));
    this.updateElement('[data-savings-amount]', this.formatMoney(pricing.savings));
    // Update discount percent label(s)
    const discountLabel = document.querySelector('[data-summary-discount-label]');
    if (discountLabel) {
      const labelRate = pricing.discountRate > 0 ? ` (${pricing.discountRate}%)` : '';
      discountLabel.textContent = `Bundle Discount${labelRate}`;
    }
    // Keep step badges aligned with configured pair/trio values
    this.applyStepBadgeTexts();
    
    const savingsText = document.querySelector('[data-savings-text]');
    if (savingsText) {
      savingsText.style.display = pricing.savings > 0 ? 'block' : 'none';
    }
    
    this.updateElement('[data-sidebar-subtotal]', this.formatMoney(pricing.subtotal));
    this.updateElement('[data-sidebar-savings]', this.formatMoney(pricing.savings));
    this.updateElement('[data-sidebar-total]', this.formatMoney(pricing.total));
    
    this.updateElement('[data-summary-subtotal]', this.formatMoney(pricing.subtotal));
    this.updateElement('[data-summary-discount]', '-' + this.formatMoney(pricing.savings));
    this.updateElement('[data-summary-total]', this.formatMoney(pricing.total));
    this.updateElement('[data-cart-total]', this.formatMoney(pricing.total));
  }

  applyStepBadgeTexts() {
    try {
      const chairBadge = document.querySelector('[data-step="1"] .bundle-step-badge');
      if (chairBadge) chairBadge.textContent = `SAVE up to ${this.bundlePairDiscount}%`;
      const deskBadge = document.querySelector('[data-step\="2\"] .bundle-step-badge');
      if (deskBadge) deskBadge.textContent = `SAVE up to ${this.bundlePairDiscount}%`;
      // Add-ons badge set in liquid (includes "on add-ons"); leave unchanged
    } catch (_) {}
  }

  calculatePricing() {
    let subtotal = 0;            // full subtotal (all items)
    let eligibleSubtotal = 0;     // items that can be discounted
    let lastAddonPrice = 0;
    
    if (this.selectedItems.chair) {
      subtotal += this.selectedItems.chair.price;
      eligibleSubtotal += this.selectedItems.chair.price;
    }
    if (this.selectedItems.desk) {
      subtotal += this.selectedItems.desk.price;
      eligibleSubtotal += this.selectedItems.desk.price;
    }
    if (this.selectedItems.addonSingle) {
      subtotal += this.selectedItems.addonSingle.price;
      eligibleSubtotal += this.selectedItems.addonSingle.price;
    }
    this.selectedItems.addons.forEach((addon, idx) => {
      subtotal += addon.price;
      if (idx === this.selectedItems.addons.length - 1) {
        lastAddonPrice = addon.price;
      }
    });
    // Only the last addon is discount-eligible; earlier addons are full price
    eligibleSubtotal += lastAddonPrice;

    // Tiered discount logic
    const hasChair = !!this.selectedItems.chair;
    const hasDesk = !!this.selectedItems.desk;
    const hasAddon = this.selectedItems.addons && this.selectedItems.addons.length > 0;
    let discountRate = 0;
    if (hasChair && hasDesk) {
      discountRate = hasAddon ? this.bundleTrioDiscount : this.bundlePairDiscount;
    }

    const discountAmount = (eligibleSubtotal * discountRate) / 100;
    const total = subtotal - discountAmount;
    
    return { subtotal, savings: discountAmount, total, discountRate };
  }

  // Sidebar
  updateSidebar() {
    const sidebarItems = document.querySelector('[data-sidebar-items]');
    if (!sidebarItems) return;
    
    let html = '';
    let hasItems = false;
    
    if (this.selectedItems.chair) {
      hasItems = true;
      html += this.createSidebarItem('Chair', this.selectedItems.chair);
    }
    
    if (this.selectedItems.desk) {
      hasItems = true;
      html += this.createSidebarItem('Desk', this.selectedItems.desk);
    }
    if (this.selectedItems.addonSingle) {
      hasItems = true;
      html += this.createSidebarItem('Add-on', this.selectedItems.addonSingle);
    }
    
    if (this.selectedItems.addons && this.selectedItems.addons.length > 0) {
      hasItems = true;
      html += this.createSidebarAddonsGroup(this.selectedItems.addons);
    }
    
    if (hasItems) {
      sidebarItems.innerHTML = html;
      this.showElement('[data-sidebar-pricing]');
      this.showElement('[data-sidebar-cta]');
    } else {
      sidebarItems.innerHTML = '<div class="sidebar-placeholder"><p>Start building your bundle</p></div>';
      this.hideElement('[data-sidebar-pricing]');
      this.hideElement('[data-sidebar-cta]');
    }
  }
  createSidebarAddonsGroup(addons) {
    const itemsHtml = addons.map(addon => `
      <div class="sidebar-item">
        <div class="sidebar-item-info">
          <strong class="sidebar-item-title">${addon.title}</strong>
          ${Array.isArray(addon.selectedOptions) && addon.selectedOptions.length ? `<small>Options: ${addon.selectedOptions.join(' / ')}</small>` : ''}
        </div>
        <div class="sidebar-item-price">${this.formatMoney(addon.price)}</div>
      </div>
    `).join('');
    return `
      <div class="sidebar-item">
        <div class="sidebar-item-info">
          <span class="sidebar-item-category">Accessories</span>
        </div>
      </div>
      ${itemsHtml}
    `;
  }

  createSidebarItem(category, item) {
    return `
      <div class="sidebar-item">
        <div class="sidebar-item-info">
          <span class="sidebar-item-category">${category}</span>
          <strong class="sidebar-item-title">${item.title}</strong>
        </div>
        <div class="sidebar-item-price">${this.formatMoney(item.price)}</div>
      </div>
    `;
  }

  // Summary
  updateSummary() {
    const summaryContainer = document.querySelector('[data-summary-items]');
    if (!summaryContainer) return;
    
    // Compute per-item discounted prices (pro‑rata split of bundle discount)
    const perItem = this.computePerItemDiscounts();
    
    let html = '';
    if (this.selectedItems.chair) {
      html += this.createSummaryItem('Chair', this.selectedItems.chair, perItem.chair);
    }
    if (this.selectedItems.desk) {
      html += this.createSummaryItem('Desk', this.selectedItems.desk, perItem.desk);
    }
    if (this.selectedItems.addonSingle) {
      html += this.createSummaryItem('Add-on', this.selectedItems.addonSingle, perItem.addonSingle);
    }
    if (this.selectedItems.addons && this.selectedItems.addons.length > 0) {
      const mapped = (this.selectedItems.addons || []).map((a, i) => {
        return Object.assign({}, a, { __discounted: Array.isArray(perItem.addons) ? perItem.addons[i] : null });
      });
      html += this.createSummaryAddonsGroup(mapped);
    }
    summaryContainer.innerHTML = html || '<p>No items selected</p>';
  }

  createSummaryItem(category, item, discountedPrice = null) {
    const optionsText = Array.isArray(item.selectedOptions) && item.selectedOptions.length
      ? `<p class="summary-item-options">Options: ${item.selectedOptions.join(' / ')}</p>`
      : '';
    const categoryHtml = category ? `<span class="summary-item-category">${category}</span>` : '';
    let priceHtml = `<div class="summary-item-price">${this.formatMoney(item.price)}</div>`;
    if (typeof discountedPrice === 'number' && discountedPrice >= 0 && discountedPrice < (Number(item.price) || 0)) {
      const pct = (() => {
        const orig = Number(item.price) || 0;
        if (orig <= 0) return null;
        const rate = Math.round((1 - discountedPrice / orig) * 100);
        return rate > 0 ? rate : null;
      })();
      const pctHtml = pct ? `<span class="summary-item-discount">(-${pct}%)</span>` : '';
      priceHtml = `<div class="summary-item-price"><s>${this.formatMoney(item.price)}</s> <span class="summary-item-price--sale">${this.formatMoney(discountedPrice)}</span> ${pctHtml}</div>`;
    }
    return `
      <div class="summary-item">
        <div class="summary-item-details">
          ${categoryHtml}
          <h4 class="summary-item-title">${item.title}</h4>
          ${optionsText}
        </div>
        ${priceHtml}
      </div>
    `;
  }

  createSummaryAddonsGroup(addons) {
      const heading = `
      <div class="summary-item summary-heading">
        <div class="summary-item-details">
          <span class="summary-item-category">Accessories</span>
        </div>
      </div>`;
    const items = addons.map(addon => this.createSummaryItem('', addon, addon.__discounted ?? null)).join('');
    return `<div class="summary-group summary-group--accessories">${heading}${items}</div>`;
  }

  /**
   * Compute per-item discounted prices based on bundle discount.
   * Distributes the bundle-level discount across items proportionally
   * to their pre-discount price. Returns prices in cents.
   */
  computePerItemDiscounts() {
    const sel = this.selectedItems || {};
    const result = { chair: null, desk: null, addonSingle: null, addons: [] };
    const addons = Array.isArray(sel.addons) ? sel.addons : [];
    const pricing = this.calculatePricing();
    const rate = Number(pricing.discountRate) || 0;

    // Default: original prices
    if (sel.chair) result.chair = sel.chair.price;
    if (sel.desk) result.desk = sel.desk.price;
    if (sel.addonSingle) result.addonSingle = sel.addonSingle.price;
    result.addons = addons.map(a => a.price);

    if (rate <= 0) return result;

    const applyRate = (amount) => {
      const cut = Math.round((amount * rate) / 100);
      return Math.max(0, amount - cut);
    };

    if (sel.chair) result.chair = applyRate(sel.chair.price);
    if (sel.desk) result.desk = applyRate(sel.desk.price);
    if (sel.addonSingle) result.addonSingle = applyRate(sel.addonSingle.price);

    if (addons.length > 0) {
      const lastIdx = addons.length - 1;
      result.addons = addons.map((a, idx) => (idx === lastIdx ? applyRate(a.price) : a.price));
    }

    return result;
  }
  // Cart
  async addBundleToCart() {
    const items = [];
    const idToLabel = new Map(); // variantId -> human label for error messages
    
    // Add-ons first (fail fast), then chair and desk
    if (this.selectedItems.addonSingle?.variantId) {
      items.push({ id: this.selectedItems.addonSingle.variantId, quantity: 1, role: 'addon' });
      idToLabel.set(String(this.selectedItems.addonSingle.variantId), `Add-on: ${this.selectedItems.addonSingle.title}`);
    }
    
    this.selectedItems.addons.forEach(addon => {
      if (addon.variantId) {
        items.push({ id: addon.variantId, quantity: 1, role: 'addon' });
        idToLabel.set(String(addon.variantId), `Add-on: ${addon.title}`);
      }
    });
    
    if (this.selectedItems.chair?.variantId) {
      items.push({ id: this.selectedItems.chair.variantId, quantity: 1, role: 'chair' });
      idToLabel.set(String(this.selectedItems.chair.variantId), `Chair: ${this.selectedItems.chair.title}`);
    }
    
    if (this.selectedItems.desk?.variantId) {
      items.push({ id: this.selectedItems.desk.variantId, quantity: 1, role: 'desk' });
      idToLabel.set(String(this.selectedItems.desk.variantId), `Desk: ${this.selectedItems.desk.title}`);
    }
    
    if (items.length === 0) {
      this.showNotification('Please select items for your bundle', 'error');
      return;
    }
    
      const addBtn = document.querySelector('[data-add-to-cart]');
    const originalBtnHtml = addBtn ? addBtn.innerHTML : '';
    try {
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.innerHTML = 'Adding to Cart...';
      }
      
      // Mark cart with builder attributes (for Functions/Scripts scoping)
      const hasAddon = this.selectedItems.addons && this.selectedItems.addons.length > 0;
      const bundleType = hasAddon ? 'trio' : 'pair';
      if (!this.bundleId) {
        this.bundleId = `BB-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
      }
      try {
        await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _bundle_builder: '1', _bundle_id: this.bundleId, _bundle_type: bundleType } })
        });
      } catch (_) {}

      // Combine duplicate variant IDs to avoid multiple requests for the same ID
      const itemsWithProps = items.map(it => ({
        id: it.id,
        quantity: it.quantity || 1,
        properties: {
          _bundle_builder: '1',
          _bundle_id: this.bundleId,
          _bundle_type: bundleType,
          _bundle_role: it.role
        }
      }));
      const combinedItems = this.combineItemsById(itemsWithProps);

      // Add items one-by-one to gracefully handle partial failures (e.g., max quantity already in cart)
      const results = await this.addItemsIndividually(combinedItems, idToLabel);

      if (results.failCount > 0) {
        // Rollback any successful adds to keep the bundle all-or-nothing
        await this.rollbackAdds(results.successAdds);
        const primaryMsg = results.failMessages[0] || 'Unable to add bundle to cart.';
        this.showNotification(primaryMsg, 'warning');
        return; // do not redirect
      }

      // All succeeded
        this.showNotification('Bundle added to cart!', 'success');
        document.dispatchEvent(new CustomEvent('cart:updated'));
      setTimeout(() => { window.location.href = '/cart'; }, 800);
    } catch (error) {
      console.error('Error:', error);
      this.showNotification('Error adding bundle to cart', 'error');
    } finally {
      if (addBtn) {
        addBtn.disabled = false;
        addBtn.innerHTML = originalBtnHtml || 'Add to Cart';
      }
    }
  }

  combineItemsById(items) {
    const map = new Map();
    items.forEach(it => {
      const propsKey = it.properties ? JSON.stringify(it.properties) : '';
      const key = `${String(it.id)}|${propsKey}`;
      const existing = map.get(key);
      if (existing) {
        existing.quantity += it.quantity || 1;
      } else {
        map.set(key, { id: it.id, quantity: it.quantity || 1, properties: it.properties });
      }
    });
    return Array.from(map.values());
  }

  async addItemsIndividually(items, idToLabel = new Map()) {
    let successCount = 0;
    let failCount = 0;
    const successAdds = [];
    const failMessages = [];
    for (const item of items) {
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [item] })
        });
        if (res.ok) {
          successCount++;
          successAdds.push({ id: item.id, quantity: item.quantity || 1 });
        } else {
          let message = 'Error adding item to cart';
          try {
            const data = await res.json();
            message = data && (data.description || data.message) || message;
          } catch (_) {}
          if (/maximum quantity/i.test(message)) {
            const label = idToLabel.get(String(item.id)) || 'This item';
            message = `${label}: maximum quantity already in your cart.`;
          }
          failMessages.push(message);
          failCount++;
          break; // fail-fast: stop adding any more items
        }
      } catch (e) {
        console.error('Add item failed:', e);
        failMessages.push('Network error adding item to cart');
        failCount++;
        break; // fail-fast on network errors too
      }
    }
    return { successCount, failCount, successAdds, failMessages };
  }

  async rollbackAdds(successAdds) {
    if (!successAdds || successAdds.length === 0) return;
    try {
      const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!cartRes.ok) return;
      const cart = await cartRes.json();

      // For each successful add, remove the same quantity from the cart
      for (const added of successAdds) {
        let toRemove = added.quantity || 1;
        // Prefer matching by both variant id and our bundle id to avoid touching other items
        let lines = (cart.items || [])
          .filter(line => line.variant_id === added.id && line.properties && line.properties._bundle_id === this.bundleId)
          .map(line => ({ key: String(line.key || ''), quantity: line.quantity }));
        // Fallback to variant id only if none match bundle id
        if (lines.length === 0) {
          lines = (cart.items || [])
            .filter(line => line.variant_id === added.id)
            .map(line => ({ key: String(line.key || ''), quantity: line.quantity }));
        }
        for (const line of lines) {
          if (toRemove <= 0) break;
          const newQty = Math.max(0, line.quantity - toRemove);
          const removeAmount = line.quantity - newQty;
          toRemove -= removeAmount;
          // Apply change for this line
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: line.key, quantity: newQty })
          });
        }
      }
    } catch (e) {
      console.error('Rollback failed:', e);
    }
  }

  // Utilities
  updateElement(selector, value) {
    const element = document.querySelector(selector);
    if (element) element.textContent = value;
  }

  showElement(selector) {
    const element = document.querySelector(selector);
    if (element) element.style.display = 'block';
  }

  hideElement(selector) {
    const element = document.querySelector(selector);
    if (element) element.style.display = 'none';
  }

  formatMoney(cents) {
    const amount = typeof cents === 'number' ? cents / 100 : parseFloat(cents) / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `bundle-notification bundle-notification-${type}`;
    notification.innerHTML = `<div class="bundle-notification-content"><p>${message}</p></div>`;
    
    document.body.appendChild(notification);
    setTimeout(() => notification.classList.add('show'), 100);
    
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('[data-bundle-builder]')) {
    new EnhancedBundleBuilder();
  }
});
